import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  signal,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { toObservable } from '@angular/core/rxjs-interop';
import { MatMenuTrigger } from '@angular/material/menu';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { translate, TranslocoService } from '@jsverse/transloco';
import {
  CompactType,
  DisplayGrid,
  GridsterComponent,
  GridsterConfig,
  GridsterItem,
  GridsterItemComponentInterface,
  GridType,
} from 'angular-gridster2';
import moment from 'moment';

import hash from 'object-hash';
import { BehaviorSubject, Subject } from 'rxjs';
import { skip, takeUntil, tap } from 'rxjs/operators';
import { v4 as UUIDv4 } from 'uuid';

import { EnumErrorStates, ErrorObject, TabObject } from '@inmoment/the-kitchen';

import { SimpleDialogButtonColor } from 'app/components/simple-dialog.component';
import { ActionPlanConstants, Constants } from 'app/constants/constants';
import {
  AnalyticItemFilterSets,
  FilterService,
} from 'app/modules/collection/services/filter.service';
import { CustomFieldsService } from 'app/modules/field/custom-fields.service';
import { FullStoryService, PageNames } from 'app/modules/full-story/services/full-story.service';
import { NewDashboardData } from 'app/modules/project/components/new-dashboard/new-dashboard.component';
import { ProjectCollectionActionsComponent } from 'app/modules/project/components/project-collection-actions/project-collection-actions.component';
import {
  BigNumberMetadataDialogOptions,
  BigNumberMetadataSelectorDialogComponent,
} from 'app/modules/widget/components/big-number-metadata-selector-dialog/big-number-metadata-selector-dialog.component';
import { MAX_DATE_MARKER } from 'app/modules/widget/components/chart-options-dialog/chart-options-dialog.component';
import {
  GridAnalyticItemSelectorDialogOptions,
  GridAnalyticItemSelectorDialogResult,
  setGridAnalyticItemSelectorDialogResultOnPayload,
} from 'app/modules/widget/components/grid-analytic-item-selector-dialog/grid-analytic-item-selector-dialog.component';
import {
  JourneyAnalyticItemSelectorDialogOptions,
  JourneyAnalyticItemSelectorDialogResult,
} from 'app/modules/widget/components/journey-analytic-item-selector-dialog/journey-analytic-item-selector-dialog.component';
import {
  AbstractWidgetComponent,
  WIDGET_MIN_WIDTH,
} from 'app/modules/widget/components/widget/abstract-widget.component';
import {
  hasSectionFilters,
  WidgetComponent,
} from 'app/modules/widget/components/widget/widget.component';
import { WidgetAddDialogOptions } from 'app/modules/widget/components/widget-add/widget-add.component';
import {
  DateMarker,
  WidgetChartComponent,
  WidgetChartOptions,
} from 'app/modules/widget/components/widget-chart/widget-chart.component';
import {
  WidgetAnalysisDataSource,
  WidgetKeyDriverAnalysisDataSource,
} from 'app/modules/widget/components/widget-data-source.component';
import { WidgetDocumentListComponent } from 'app/modules/widget/components/widget-document-list/widget-document-list.component';
import { WidgetGridOptions } from 'app/modules/widget/components/widget-grid/widget-grid.component';
import { PptxService } from 'app/modules/widget/services/pptx.service';
import {
  ExpansionPanelType,
  WidgetCommentsHandlerService,
} from 'app/modules/widget/services/widget-comments-handler.service';
import { LexDatePipe } from 'app/pipes/lex-date.pipe';
import { AnalyticsService, TimerFn } from 'app/providers/analytics.service';
import { AliasResourceType, AliasService } from 'app/providers/bridge-api/alias.service';
import { AuthService, LogoutInterceptor, Session } from 'app/providers/bridge-api/auth.service';
import {
  Dashboard,
  DashboardService,
  Widget,
  WidgetGroup,
} from 'app/providers/bridge-api/dashboard.service';
import { Preferences, SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import { RealtimeConnection, RealtimeMessage } from 'app/providers/realtime.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { AiInsightsGroupFeedService } from 'app/providers/storage-api/ai-insights-group-feeds.service';
import { AnalysisGroupService } from 'app/providers/storage-api/analysis-group-service';
import { AnalysisService } from 'app/providers/storage-api/analysis.service';
import { StorageAccountService } from 'app/providers/storage-api/blender-account.service';
import { CollectionService } from 'app/providers/storage-api/collection.service';
import {
  InteractiveSmartSummary,
  InteractiveSmartSummaryService,
} from 'app/providers/storage-api/interactive-smart-summary.service';
import { PredictiveService } from 'app/providers/storage-api/predictive.service';
import { getAnalysisDisplayName } from 'app/providers/storage-api/utils';
import { AITool, HasTitleAndAITools } from 'app/services/abstract-ai-tool';
import { AIToolService } from 'app/services/aitool.service';
import { AlertService } from 'app/services/alert.service';
import { ContextBridgeService } from 'app/services/context-bridge.service';
import { DialogService } from 'app/services/dialog.service';
import { LocalStorageKey, LocalStorageService } from 'app/services/local-storage.service';
import { LoggerService } from 'app/services/logger.service';
import {
  DisableMethodTelemetry,
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { TimezoneService } from 'app/services/timezone.service';
import { ErrorObjectUtils } from 'app/utils/error-object.utils';
import { Utils } from 'app/utils/utils';
import { ModalCloseType } from '../../../../enums';
import { WidgetDataSource, WidgetDriverAnalysisOptions } from '../../../../models';
import { DASHBOARD_WIDTH, DashboardPage } from '../../../../providers/bridge-api/dashboard.service';
import { DashboardMigrationService } from '../../../../providers/dashboard-migration.service';
import { ContentChannelService } from '../../../../providers/storage-api/content-channel.service';
import {
  DEFAULT_PROJECT_COLOR_PALETTE,
  Project,
  ProjectService,
} from '../../../../providers/storage-api/project.service';
import { RtrEventHandlerService } from '../../../../services/rtr-event-handler.service';
import { generalNameValidator } from '../../../../utils/customValidators';
import { FilterSet } from '../../../collection/services/filter.service';
import {
  ShareDashboardDialogComponent,
  ShareDashboardDialogOptions,
} from '../../../project/components/share-dashboard-dialog/share-dashboard-dialog.component';
import { AnalyticItemsDialogOptions } from '../../../widget/components/analytic-item-selector/analytic-item-selector.component';
import {
  FieldSelectDialogResult,
  ImpactMetric,
} from '../../../widget/components/widget-impact/rating-field-selector-dialog/rating-field-selector-dialog.component';
import { AbstractChartReport } from '../../../widget/services/chart-report/chart-report-abstract/chart-report-abstract';
import { ChartReportService } from '../../../widget/services/chart-report.service';
import { SaveDashboardEvent } from '../../components/dashboard-options/dashboard-options.component';
import { DashboardPageRenameDialogOptions } from '../../components/dashboard-page-rename-dialog/dashboard-page-rename-dialog.component';
import { SavedView } from '../../models/saved-view.model';

import { ActionPlanSidePaneService } from '../../services/action-plan-side-pane.service';
import { AnalyticItemTypes } from '../../services/analytic-item-types';
import { ChartCustomColorService } from '../../services/chart-custom-color.service';
import { ComparisonHandlerService } from '../../services/comparison-handler-service';
import { DashboardPageManagerService } from '../../services/dashboard-page-manager.service';
import { PinningService } from '../../services/pinning.service';
import { DashboardStore } from '../../stores/dashboard.store';
import {
  DATASOURCE_GROUP_KEY,
  DatasourceManagerStore,
} from '../../stores/datasource-manager.store';
import { SelectModeStore } from '../../stores/select-mode.store';
import {
  createWidgetGroup,
  deleteWidgetGroup,
  getDashboardLevelWidgetGroup,
  isDashboardLevelWidgetGroup,
  widgetGroupSorter,
  WidgetGroupStore,
} from '../../stores/widget-group.store';

export interface CustomItemColor {
  pointId: string;
  color: string;
}

export interface DashboardPageChangeData {
  updated: Set<DashboardPage>;
  added: Set<DashboardPage>;
  deleted: Set<DashboardPage>;
  reordered?: boolean;
}

export interface WidgetGroupSidePanelData {
  dashboardIsLocked: boolean;
  sidePanelIsOpened?: boolean;
}

class WidgetLoadingManager {
  loadingWidgets: any = {};
  loading$ = new BehaviorSubject<number>(0);

  onBeginLoading(widgetId: string) {
    this.loadingWidgets[widgetId] = 1;
    this.next();
  }

  onDoneLoading(widgetId: string) {
    delete this.loadingWidgets[widgetId];
    this.next();
  }

  get currentlyLoading(): number {
    return Object.keys(this.loadingWidgets).length;
  }

  next() {
    this.loading$.next(this.currentlyLoading);
  }
}

@EnableClassTelemetry()
@Component({
  selector: 'app-dashboard-view',
  templateUrl: './dashboard-view.component.html',
  styleUrls: ['./dashboard-view.component.scss'],
  providers: [ChartCustomColorService],
  standalone: false,
  host: { '(window:resize)': 'onWindowResize()' },
})
export class DashboardViewComponent
  extends HasTitleAndAITools
  implements OnInit, OnDestroy, LogoutInterceptor, AfterViewInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  @Input() public activeSavedView?: SavedView;
  @Input() public showSavedViewBanner = false;

  readonly widgetGroupStore = inject(WidgetGroupStore);
  readonly selectModeStore = inject(SelectModeStore);
  readonly datasourceManagerStore = inject(DatasourceManagerStore);
  readonly dashboardStore = inject(DashboardStore);

  @Output() widgetGroupRefreshEmit: EventEmitter<boolean> = new EventEmitter();
  @Output() dashboardUnsavedEmit: EventEmitter<boolean> = new EventEmitter();
  @Output() dashboardPageChangedEmit: EventEmitter<number> = new EventEmitter();
  @Output() unsavedPagesEmit: EventEmitter<DashboardPageChangeData> = new EventEmitter();
  @Output() widgetsCountChanges: EventEmitter<Dashboard> = new EventEmitter();
  @Output() setWidgetGroupPanel: EventEmitter<WidgetGroupSidePanelData> = new EventEmitter();
  @Output() onActivitybarInteraction: EventEmitter<any> = new EventEmitter();
  @Output() dashboardParametersChange = new EventEmitter<{
    groupId: string;
    project: Project;
    dashboardId: string;
  }>();
  @Output() savedViewBannerSave = new EventEmitter<void>();
  @Output() savedViewBannerSaveAs = new EventEmitter<void>();
  @Output() savedViewBannerRevert = new EventEmitter<void>();
  @Output() savedViewBannerClose = new EventEmitter<void>();

  @ViewChild('pageMenuTriggerElement') pageMenuTriggerElement: MatMenuTrigger;
  @ViewChild('pageMenuTriggerElement', { read: ElementRef })
  pageMenuTriggerNativeElement: ElementRef;
  @ViewChildren('elementWidget') private elementWidget: QueryList<WidgetComponent>;
  @ViewChild('elementDashboardWrapper', { read: ElementRef })
  private elementDashboardWrapper: ElementRef<HTMLDivElement>;
  @ViewChild('actionBarContainer', { read: ElementRef })
  private elementActionBar: ElementRef<HTMLDivElement>;
  @ViewChild('elementDashboard') private elementDashboard: GridsterComponent;
  @ViewChild(ProjectCollectionActionsComponent)
  collectionActions: ProjectCollectionActionsComponent;
  @ViewChild('widgetAddBtn') widgetAddBtnRef: ElementRef;

  private readonly GRID_MARGIN = 10;
  // Row height calculation defaults
  private static readonly AVAILABLE_HEIGHT_MIN = 300;
  private static readonly WINDOW_HEIGHT_PADDING = 160; // approximate header/footer/controls height
  private static readonly TARGET_ROW_COUNT = 12; // aim for ~12 rows of content
  private static readonly ROW_HEIGHT_MIN = 40;
  private static readonly ROW_HEIGHT_MAX = 60;

  // Widget expansion constants
  private static readonly SCROLL_DELAY_MS = 300;
  private static readonly MIN_EXPANDED_WIDGET_HEIGHT_PX = 300;
  projectRoute: string;
  public dashboardId: string;
  public projectId: string;
  public project: Project;
  public originalDashboard: Dashboard;
  public dashboard: Dashboard;
  public isLoading = { dashboard: true, saveEdit: false };
  public changedWidgets = new Set<string>();
  public pageChangeData: DashboardPageChangeData = {
    updated: new Set<DashboardPage>(),
    added: new Set<DashboardPage>(),
    deleted: new Set<DashboardPage>(),
  };

  public getAnalysisDisplayName = getAnalysisDisplayName;
  public session: Session;
  unsub$ = new Subject<void>();
  hasLoadedOnce: boolean;
  realtimeHandlers: RealtimeConnection[] = [];

  public dashboardName = '';
  public ModalCloseType = ModalCloseType;
  private existingDashboardNames: string[] = [];
  private originalTitle: string;

  private lastAppliedRowHeight?: number;
  private lastMeasuredAvailableHeight: number =
    DashboardViewComponent.TARGET_ROW_COUNT * DashboardViewComponent.ROW_HEIGHT_MAX;
  private availableRowCount: number = DashboardViewComponent.TARGET_ROW_COUNT;
  private wrapperResizeObserver?: ResizeObserver;

  private savedViewBaselineHash?: string;

  showReloadRequiredModal = false;
  private loadedDashboardRevision?: number;
  private dashboardUpdatePollingInterval?: ReturnType<typeof setInterval>;

  isReadOnly;
  isAdmin;
  isSharedSession;
  isOwner;
  isExecutiveUser;

  customSortMode = {
    enabled: false,
    widget: undefined,
  };
  widgetLoadingManager = new WidgetLoadingManager();
  scrollPosition: number;
  isAnyWidgetDatasourceRemoved = false;
  canAddWidget = false;
  isAnyWidgetDirty = false;
  comparisonList = [];
  readonly DEFAULT_RATING_MODAL_WIDTH = '600px';
  ratingModalWidth = this.DEFAULT_RATING_MODAL_WIDTH;

  reportTiming: TimerFn;
  inSaveFlow = false;
  private redrawIntervals = new Set<any>();
  dashboardColorDictionary: CustomItemColor[] = [];
  public dashboardUnsaved = false;
  public dashboardNameStatus: ErrorObject = { state: EnumErrorStates.SUCCESS };

  dashboardOptions: GridsterConfig = {
    allowMultiLayer: true,
    gridType: GridType.VerticalFixed,
    displayGrid: DisplayGrid.None,
    minCols: DASHBOARD_WIDTH,
    maxCols: DASHBOARD_WIDTH,
    minItemCols: WIDGET_MIN_WIDTH,
    minItemRows: 1,
    defaultLayerIndex: 1,
    compactType: CompactType.None,
    fixedRowHeight: DashboardViewComponent.ROW_HEIGHT_MAX,
    margin: this.GRID_MARGIN,
    pushItems: true,
    pushResizeItems: true,
    maxRows: 500, //for more information see DASH-9519
    draggable: {
      enabled: false,
      ignoreContent: true,
      stop: (item: GridsterItem) => {
        const [prevX, prevY] = [item.x, item.y];

        setTimeout(() => {
          if (prevX !== item.x || prevY !== item.y) {
            this.onDropWidget(item);
          }
        });
      },
    },
    resizable: {
      enabled: false,
      start: (item: GridsterItem) => {
        this.gridsterItemResizing = item;
      },
      stop: () => {
        this.gridsterItemResizing = undefined;
      },
    },
    itemResizeCallback: (item: GridsterItem) => {
      const [prevCols, prevRows] = [item.cols, item.rows];

      setTimeout(() => {
        if (
          (this.gridsterItemResizing && this.gridsterItemResizing !== item) ||
          prevCols !== item.cols ||
          prevRows !== item.rows
        ) {
          this.onResizeWidget(item);
        }
      }, 300);
    },
  };
  dashboardPages: TabObject[] = [{ title: '…', id: '1' }];
  currentPageId = 0;
  currentPageTabId = '0';
  currentPageWidgets: Widget[] = [];
  currentPageWidgetsShowing = false;
  isCurrentPageSpecial;
  arePageWidgetDetailsHidden: boolean;
  // Stable per-page reading order (left-to-right, top-to-bottom) of widget IDs
  private pageLayoutOrderById: Map<number, string[]> = new Map();
  // Small delay to re-apply layout after Gridster settles
  private readonly REFLOW_REAPPLY_DELAY_MS = 60;

  private getSavedOrderForCurrentPage(): string[] | undefined {
    const saved = this.pageLayoutOrderById.get(this.currentPageId);
    if (!saved || !saved.length) {
      return undefined;
    }
    // Filter out IDs that no longer exist on the current page
    const currentIds = this.getCurrentPageWidgetIds();
    const filtered = saved.filter((id) => currentIds.includes(id));
    return filtered.length ? filtered : undefined;
  }

  private saveOrderForCurrentPage(order: string[]): void {
    this.pageLayoutOrderById.set(this.currentPageId, [...order]);
  }

  private getCurrentPageWidgetIds(): string[] {
    return this.currentPageWidgets.map((w) => w.id);
  }

  private getCurrentPageWidgetIdsByVisualOrder(): string[] {
    return [...this.currentPageWidgets].sort((a, b) => a.y - b.y || a.x - b.x).map((w) => w.id);
  }

  private getOrInitOrderForCurrentPage(): string[] {
    const saved = this.getSavedOrderForCurrentPage();
    if (saved?.length) {
      return saved;
    }
    const current = this.getCurrentPageWidgetIds();
    this.saveOrderForCurrentPage(current);
    return current;
  }

  private requestReflowWithAppended(widgetId: string): void {
    // ensure unique and appended
    const base = this.getOrInitOrderForCurrentPage().filter((id) => id !== widgetId);
    this.saveOrderForCurrentPage([...base, widgetId]);
  }

  private requestReflowWithRemoved(widgetId: string): void {
    this.saveOrderForCurrentPage(
      this.getOrInitOrderForCurrentPage().filter((id) => id !== widgetId)
    );
  }

  showPageReorderModal = false;
  showPageRenameModal = false;
  pageRenameOptions: DashboardPageRenameDialogOptions;

  showDashboardOptions = false;
  readonly canAddPage = signal(false);
  readonly availableWidgetsCount = computed(() => {
    const mode = this.selectModeStore.mode();
    if (!mode) {
      return 0;
    }
    return this.getAvailableWidgets().length;
  });

  gridsterItemResizing: GridsterItem;
  editModeHistory: GridsterItem[][];
  editModeHistoryPointer: number;

  showApplyComparisonModal;
  applyComparisonModalData;

  showWidgetAddDialog;
  widgetAddDialogData: WidgetAddDialogOptions;

  showRatingFieldSelectorModal;
  ratingFieldSelectorData: {
    dataSource;
    ratingFieldName;
    cancelButtonText;
    metadataFields;
    resolveFn;
  };

  showNewDashboardModal: boolean;
  newDashboardModalData: NewDashboardData;
  revertSaveAsDashboard: boolean;

  public preferences: Preferences;
  public showTimezoneModal = false;
  public dashboardTimezone: string;
  public timezoneOffset;
  public timezoneTooltip;
  readonly shareButtonTooltip = computed(() => {
    const currentMode = this.selectModeStore.mode();
    if (currentMode) {
      return {
        text: translate('dashboard-view.tooltip.action-disabled-bulk-apply'),
        direction: 5,
      };
    }
    return {
      text: this.dashboard?.payload.isExecutive
        ? translate('dashboard-view.tooltip.share-enabled')
        : translate('dashboard-view.tooltip.share-disabled'),
      direction: 5,
    };
  });

  readonly settingsButtonTooltip = computed(() => {
    const currentMode = this.selectModeStore.mode();
    if (currentMode) {
      return {
        text: translate('dashboard-view.tooltip.action-disabled-bulk-apply'),
        direction: 5,
      };
    }
    return {
      text: this.isDashboardNew
        ? translate('dashboard-view.tooltip.action-disabled-unsaved')
        : translate('dashboard-view.tooltip.options-enabled'),
      direction: 5,
    };
  });

  readonly deleteButtonTooltip = computed(() => {
    const currentMode = this.selectModeStore.mode();
    if (currentMode) {
      return {
        text: translate('dashboard-view.tooltip.action-disabled-bulk-apply'),
        direction: 5,
      };
    }
    return {
      text: translate('dashboard-view.tooltip.delete-enabled'),
      direction: 5,
    };
  });

  gridAnalyticItemSelectorDialogOptions: GridAnalyticItemSelectorDialogOptions;
  showGridAnalyticItemSelectorDialog = false;

  journeyAnalyticItemSelectorDialogOptions: JourneyAnalyticItemSelectorDialogOptions;
  showJourneyAnalyticItemSelectorDialog = false;

  readonly addNewTabButton = computed(() => {
    const canAddPage = this.canAddPage();
    const isSelectMode = !!this.selectModeStore.mode();
    const tooltipText = !canAddPage
      ? translate('dashboard-view.tooltip.cannot-add-page')
      : translate('dashboard-view.tooltip.add-new-page');
    return {
      icon: 'add_circle' as const,
      onClick: () => this.addPage(),
      disabled: !canAddPage || isSelectMode,
      tooltip: {
        text: tooltipText,
      },
    };
  });

  /**
   * Need this var so we don't get ExpressionChangedAfterViewCheckedError b/c of the widget add button ngIf binding in pill wrapper
   * Since isReadOnly assign is in an asyc function it exceeds the lifecycle hooks length (onInit) && the chage detection triggering is halted long enough
   * isReadOnly is gonna be assigned after view init is done -> leading to the error
   **/
  refreshDone = false;
  hasSmartSummaryResources = false;
  shouldIgnoreWidgetChange = false;
  isExpansionPanelShowing = false;
  skipPinningInit: boolean;
  private expansionPanelShowingOnWidget: string;
  private expansionPanelTypeOnWidget: ExpansionPanelType;
  ratingTitle: string;
  showAnalyticItemModal = false;
  analyticItemModalOptions: AnalyticItemsDialogOptions;
  private chartReportService = inject(ChartReportService);

  constructor(
    protected titleService: Title,
    private route: ActivatedRoute,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router,
    private alertService: AlertService,
    private dialogService: DialogService,
    private pptxService: PptxService,
    private dashboardService: DashboardService,
    private analysisService: AnalysisService,
    private analysisGroupService: AnalysisGroupService,
    private filterService: FilterService,
    private accountService: StorageAccountService,
    private groupService: GroupService,
    private authService: AuthService,
    private collectionService: CollectionService,
    private projectService: ProjectService,
    private googleAnalyticsService: AnalyticsService,
    private logger: LoggerService,
    private chartCustomColorService: ChartCustomColorService,
    private fullStoryService: FullStoryService,
    private lexDatePipe: LexDatePipe,
    private pageManager: DashboardPageManagerService,
    private renderer: Renderer2,
    public localStorageService: LocalStorageService,
    private aliasService: AliasService,
    private preferencesService: SemantriaPreferences,
    private comparisonService: ComparisonHandlerService,
    private timezoneService: TimezoneService,
    private predictiveService: PredictiveService,
    private actionPlanSidePaneService: ActionPlanSidePaneService,
    private interactiveSmartSummaryService: InteractiveSmartSummaryService,
    private pinningService: PinningService,
    protected aiToolService: AIToolService,
    private translocoService: TranslocoService,
    protected bridgeService: ContextBridgeService,
    private customFieldService: CustomFieldsService,
    private commentsHandlerService: WidgetCommentsHandlerService,
    private contentChannelService: ContentChannelService,
    private rtrHandlerService: RtrEventHandlerService,
    private aiInsightsGroupFeedService: AiInsightsGroupFeedService
  ) {
    super(titleService, aiToolService, bridgeService);
    this.skipPinningInit = !!history.state?.skipPinningInit;
    delete history.state?.skipPinningInit;
    toObservable(this.dashboardStore.dirtyCount)
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => this.guardedIsUnsaved());
  }

  ngAfterViewInit(): void {
    // Apply an initial dynamic row height once the view and Gridster are ready
    setTimeout(() => this.applyDynamicRowHeight(), 0);

    // Observe wrapper size changes (e.g., when moving window between monitors)
    const wrapperEl = this.elementDashboardWrapper?.nativeElement as HTMLDivElement | undefined;
    if (wrapperEl && 'ResizeObserver' in window) {
      this.wrapperResizeObserver = new ResizeObserver(() => this.applyDynamicRowHeight());
      this.wrapperResizeObserver.observe(wrapperEl);
    }
  }

  onWindowResize(): void {
    this.applyDynamicRowHeight();
  }

  private applyDynamicRowHeight(): void {
    const availableHeight = this.calculateAvailableHeight();
    const newRowHeight = this.calculateOptimalRowHeight(availableHeight);

    if (this.shouldSkipRowHeightUpdate(newRowHeight)) {
      return;
    }

    this.updateRowHeight(newRowHeight);
    this.updateRowCapacity();
    this.notifyGridsterOfChanges();
  }

  private calculateAvailableHeight(): number {
    const wrapperHeight = this.getDashboardWrapperHeight();
    const actionBarHeight = this.getActionBarHeight();
    const adjustedHeight = Math.max(0, wrapperHeight - actionBarHeight);

    const availableHeight = Math.max(
      DashboardViewComponent.AVAILABLE_HEIGHT_MIN,
      Math.floor(adjustedHeight)
    );

    this.lastMeasuredAvailableHeight = availableHeight;
    return availableHeight;
  }

  private getDashboardWrapperHeight(): number {
    const wrapperEl = this.elementDashboardWrapper?.nativeElement as HTMLDivElement | undefined;
    return (
      wrapperEl?.clientHeight ?? window.innerHeight - DashboardViewComponent.WINDOW_HEIGHT_PADDING
    );
  }

  private getActionBarHeight(): number {
    return this.elementActionBar?.nativeElement?.offsetHeight ?? 0;
  }

  private calculateOptimalRowHeight(availableHeight: number): number {
    const targetRows = DashboardViewComponent.TARGET_ROW_COUNT;
    return Math.max(
      DashboardViewComponent.ROW_HEIGHT_MIN,
      Math.min(DashboardViewComponent.ROW_HEIGHT_MAX, Math.round(availableHeight / targetRows))
    );
  }

  private shouldSkipRowHeightUpdate(newRowHeight: number): boolean {
    return this.lastAppliedRowHeight === newRowHeight;
  }

  private updateRowHeight(rowHeight: number): void {
    this.lastAppliedRowHeight = rowHeight;
    this.dashboardOptions.fixedRowHeight = rowHeight;
  }

  private updateRowCapacity(): void {
    const margin = this.dashboardOptions.margin ?? 0;
    const rowHeightWithMargin = this.lastAppliedRowHeight + margin;

    const estimatedCapacity = this.calculateRowCapacity(rowHeightWithMargin, margin);
    const maxRowsLimit = this.dashboardOptions.maxRows ?? estimatedCapacity;

    this.availableRowCount = Math.max(1, Math.min(estimatedCapacity, maxRowsLimit));
  }

  private calculateRowCapacity(rowHeightWithMargin: number, margin: number): number {
    if (rowHeightWithMargin <= 0) {
      return DashboardViewComponent.TARGET_ROW_COUNT;
    }

    return Math.floor((this.lastMeasuredAvailableHeight + margin) / rowHeightWithMargin);
  }

  private notifyGridsterOfChanges(): void {
    const api = this.dashboardOptions.api || this.elementDashboard?.options?.api;
    api?.optionsChanged?.();
  }

  /**
   * Returns the number of Gridster rows a widget should occupy while showing an expansion panel.
   */
  public getExpandedRowsForWidget(widget: Widget): number {
    const minimumRows = this.getMinimumWidgetRows(widget);
    const minimumRowsForHeight = this.getMinimumRowsForExpandedHeight();
    const effectiveMinimumRows = Math.max(minimumRows, minimumRowsForHeight);
    const maximumAllowedRows = this.getMaximumAllowedRows();

    return Math.min(maximumAllowedRows, Math.max(effectiveMinimumRows, this.availableRowCount));
  }

  private getMinimumRowsForExpandedHeight(): number {
    if (!this.lastAppliedRowHeight) {
      return 1;
    }

    const rowsNeeded = Math.ceil(
      DashboardViewComponent.MIN_EXPANDED_WIDGET_HEIGHT_PX / this.lastAppliedRowHeight
    );
    return Math.max(1, rowsNeeded);
  }

  private getMinimumWidgetRows(widget: Widget): number {
    return widget.minItemRows ?? this.dashboardOptions.minItemRows ?? 1;
  }

  private getMaximumAllowedRows(): number {
    return this.dashboardOptions.maxRows ?? this.availableRowCount;
  }

  public get widgetsLoaded(): boolean {
    return this.widgetLoadingManager.currentlyLoading === 0 && this.currentPageWidgets.length > 0;
  }

  @DisableMethodTelemetry() // gets called every second
  public guardedIsUnsaved(): void {
    let unsaved = false;

    // Don't show unsaved state when expansion panel is open
    if (this.isExpansionPanelShowing) {
      return;
    }

    if (this.dashboard && !this.selectModeStore.mode()) {
      if (!this.isReadOnly) {
        // Editable dashboards: use standard unsaved detection
        unsaved = this.isUnsaved === true;
      } else {
        // Read-only dashboards: still run the original unsaved logic to
        // keep changedWidgets and related per-widget change indicators
        // (e.g., orange borders) in sync with the original dashboard,
        // but drive the overall Saved View banner unsaved state from
        // the Saved View baseline instead.
        //
        // isUnsaved returns false for read-only dashboards and does not
        // save to cache, but it does update changedWidgets/pageChangeData.
        this.isUnsaved;

        if (this.activeSavedView) {
          unsaved = this.hasSavedViewChanges();
        }
      }
    }

    this.dashboardUnsaved = unsaved;
    this.dashboardUnsavedEmit.emit(this.dashboardUnsaved);
  }

  private hasSavedViewChanges(): boolean {
    if (!this.activeSavedView || !this.savedViewBaselineHash || !this.dashboard) {
      return false;
    }

    const currentHash = this.buildSavedViewHash();
    return currentHash !== this.savedViewBaselineHash;
  }

  private buildSavedViewHash(): string {
    if (!this.dashboard) {
      return '';
    }

    const snapshot = {
      widgets: this.dashboard.widgets ?? [],
      widgetGroups: this.dashboard.widgetGroups ?? {},
    };

    return hash(snapshot);
  }

  public get isUnsaved(): boolean {
    if (
      !this.dashboard ||
      !this.originalDashboard ||
      this.inSaveFlow ||
      this.selectModeStore.mode()
    ) {
      return false;
    }

    let unsaved =
      this.dashboard.visibility !== this.originalDashboard.visibility ||
      this.dashboard.payload.notes !== this.originalDashboard.payload.notes ||
      this.dashboard.payload.sentimentScale !== this.originalDashboard.payload.sentimentScale ||
      this.dashboard.payload.sentimentDisplay !== this.originalDashboard.payload.sentimentDisplay ||
      this.dashboard.payload.timezone !== this.originalDashboard.payload.timezone ||
      this.dashboard.payload.timezoneOffset !== this.originalDashboard.payload.timezoneOffset;

    const previousPageChangeData = {
      updated: new Set(this.pageChangeData.updated),
      added: new Set(this.pageChangeData.added),
      deleted: new Set(this.pageChangeData.deleted),
      reordered: this.pageChangeData.reordered,
    };

    this.changedWidgets.clear();
    this.pageChangeData.updated.clear();
    this.pageChangeData.added.clear();
    this.pageChangeData.deleted.clear();
    this.pageChangeData.reordered = false;

    const comparePages = (
      sourcePages: DashboardPage[],
      targetPages: DashboardPage[],
      changedSet: Set<DashboardPage>
    ) => {
      sourcePages.forEach((p) => {
        const targetPageIdx = targetPages.findIndex((op) => op.id === p.id);
        if (targetPageIdx === -1) {
          changedSet.add(p);
          unsaved = true;
        } else if (
          !Utils.deepEquals(p, targetPages[targetPageIdx]) &&
          !this.pageChangeData.updated.has(targetPages[targetPageIdx])
        ) {
          this.pageChangeData.updated.add(p);
          unsaved = true;
        }
      });
    };

    const safePages = this.dashboard.payload.pages || [];
    const safeOriginalPages = this.originalDashboard.payload.pages || [];

    comparePages(safePages, safeOriginalPages, this.pageChangeData.added);
    comparePages(safeOriginalPages, safePages, this.pageChangeData.deleted);

    const currentPageOrder = safePages
      .filter((p) => safeOriginalPages.some((op) => p.id === op.id))
      .map((p) => p.id);
    const originalPageOrder = safeOriginalPages
      .filter((p) => safePages.some((op) => p.id === op.id))
      .map((p) => p.id);
    this.pageChangeData.reordered = !Utils.deepEquals(currentPageOrder, originalPageOrder);
    unsaved ||= this.pageChangeData.reordered;

    const compareElementsFactory = (
      pageIdExtractor: (o: any) => string,
      callbackFn?: (o: any) => void
    ) => {
      return (
        pagesSource: DashboardPage[],
        sourceArray: any[],
        targetArray: any[],
        changedSet: Set<DashboardPage>
      ) => {
        sourceArray.forEach((w) => {
          const sourcePageId = pageIdExtractor(w);
          const targetElement = targetArray.find(
            (ow) => ow.id === w.id && pageIdExtractor(ow) === sourcePageId
          );

          const sourcePageOrder = sourceArray
            .filter((pw) => sourcePageId === pageIdExtractor(pw))
            .map((pw) => pw.id);
          const targetPageOrder = targetArray
            .filter((pw) => sourcePageId === pageIdExtractor(pw))
            .map((pw) => pw.id);

          let ignoreProps = [
            'refresh',
            'minItemCols',
            'minItemRows',
            'dragEnabled',
            'resizeEnabled',
            'compactEnabled',
            'layerIndex',
          ];
          if (this.shouldIgnoreWidgetChange) {
            ignoreProps = [...ignoreProps, 'cols', 'rows', 'x', 'payload'];
          }

          if (
            !targetElement ||
            !Utils.deepEquals(w, targetElement, ignoreProps) ||
            !Utils.deepEquals(sourcePageOrder, targetPageOrder)
          ) {
            callbackFn && callbackFn(w);
            if (
              !changedSet.has(this.getPageById(sourcePageId, pagesSource)) &&
              this.dashboard.payload.pages
            ) {
              this.pageChangeData.updated.add(this.getPageById(sourcePageId));
            }
            unsaved = true;
          }
        });
      };
    };

    const widgetComparator = compareElementsFactory(
      (w) => w.payload.page,
      (w) => {
        if (!this.shouldIgnoreWidgetChange) {
          this.changedWidgets.add(w.id);
        }
      }
    );

    const pageItemComparator = compareElementsFactory((i) => i.page ?? 1);

    widgetComparator(
      safePages,
      this.dashboard.widgets,
      this.originalDashboard.widgets,
      this.pageChangeData.added
    );

    widgetComparator(
      safeOriginalPages,
      this.originalDashboard.widgets,
      this.dashboard.widgets,
      this.pageChangeData.deleted
    );

    const widgetGroups = Object.values(this.dashboard.widgetGroups).sort(widgetGroupSorter);
    const originalWidgetGroups = Object.values(this.originalDashboard.widgetGroups).sort(
      widgetGroupSorter
    );

    pageItemComparator(safePages, widgetGroups, originalWidgetGroups, this.pageChangeData.added);

    pageItemComparator(
      safeOriginalPages,
      originalWidgetGroups,
      widgetGroups,
      this.pageChangeData.deleted
    );

    if (this.dashboard.dashboard_version > 10) {
      const safePins = this.dashboard.payload.pins || [];
      const safeOriginalPins = this.originalDashboard.payload.pins || [];

      if (!Utils.deepEquals(safePins, safeOriginalPins)) {
        unsaved = true;
      }
    }

    if (this.dashboard.dashboard_version > 9) {
      const safeActionPlans = this.dashboard.payload.actionPlans || [];
      const safeOriginalActionPlans = this.originalDashboard.payload.actionPlans || [];

      pageItemComparator(
        safePages,
        safeActionPlans,
        safeOriginalActionPlans,
        this.pageChangeData.added
      );

      pageItemComparator(
        safeOriginalPages,
        safeOriginalActionPlans,
        safeActionPlans,
        this.pageChangeData.deleted
      );
    }

    if (this.dashboard.dashboard_version > 8) {
      pageItemComparator(
        safePages,
        Object.values(this.dashboard.comparisons || {}),
        Object.values(this.originalDashboard.comparisons || {}),
        this.pageChangeData.added
      );

      pageItemComparator(
        safeOriginalPages,
        Object.values(this.originalDashboard.comparisons || {}),
        Object.values(this.dashboard.comparisons || {}),
        this.pageChangeData.deleted
      );
    }

    if (this.dashboard.dashboard_version > 5) {
      const isPageChangeDifferent = (previousState, currentState) => {
        const checkSetEquality = (s1, s2) => s1.size === s2.size && [...s1].every((x) => s2.has(x));

        return (
          !checkSetEquality(previousState.added, currentState.added) ||
          !checkSetEquality(previousState.deleted, currentState.deleted) ||
          !checkSetEquality(previousState.updated, currentState.updated) ||
          !!previousState.reordered !== !!currentState.reordered
        );
      };

      if (isPageChangeDifferent(previousPageChangeData, this.pageChangeData)) {
        this.unsavedPagesEmit.emit(this.pageChangeData);
      }
    }

    if (!this.isReadOnly) {
      this.saveDashboardInCache(unsaved);
    } else {
      return false;
    }
    return unsaved;
  }

  public onValidateDashboardName() {
    generalNameValidator(
      this.dashboardName,
      this.dashboardNameStatus,
      'Dashboard',
      64,
      true,
      this.existingDashboardNames,
      false
    );

    if (!this.dashboard.title.match(/^[a-z0-9-_]+$/i)) {
      ErrorObjectUtils.setError(
        this.dashboardNameStatus,
        translate('dashboard-view.alert.name-validate')
      );
    }
  }

  async ngOnInit() {
    super.onToolInit();
    const params = this.route.snapshot.paramMap;
    let groupId = params.get('groupId');
    this.projectId = params.get('projectId');
    this.dashboardId = params.get('dashboardId');
    const dashboardAlias = params.get('dashboardAlias');

    this.preferences = await this.preferencesService.getPreferences();
    this.hasSmartSummaryResources = await this.accountService.hasInteractiveSmartSummaryResources();

    let aliasedDashboard;

    if (dashboardAlias) {
      try {
        const alias = await this.aliasService.getAliasByTypeAndName(
          AliasResourceType.DASHBOARD,
          dashboardAlias
        );
        aliasedDashboard = await this.dashboardService.getDashboardById(alias.resourceId);
        groupId = GroupService.shortenGroupId(alias.groupId);
        this.projectId = aliasedDashboard.project_id;
        this.dashboardId = alias.resourceId;
      } catch (err) {
        if (err.status === 401) {
          return;
        }
        this.alertService.showErrorToast(
          translate('dashboard-view.alert.load-fail', {
            dashboardName: dashboardAlias,
          }),
          err
        );

        const homepage = this.preferences.homepage || this.groupService.getDefaultHomepage();
        this.router.navigate([homepage]);
        return;
      }
    }

    await this.groupService.refreshAndSelectActiveGroup(groupId);
    this.project = await this.projectService.getProjectById(this.projectId);
    this.dashboardParametersChange.emit({
      groupId,
      project: this.project,
      dashboardId: this.dashboardId,
    });
    this.projectRoute = `/groups/${groupId}/projects/${this.projectId}`;

    this.reportTiming = this.googleAnalyticsService.createTimer('Dashboard');
    this.datasourceManagerStore.initialize({
      analyses: this.project.analyses,
      dashboardId: this.dashboardId,
      project: this.project,
    });

    this.authService.addLogoutInterceptor(this);

    this.fullStoryService.setPageVars(PageNames.dashboard, {
      projectId: this.projectId,
      dashboardId: this.dashboardId,
    });

    try {
      this.realtimeHandlers = [
        this.collectionService.on(this.projectId, this.handleCollectionsEvent.bind(this)),
        this.analysisService.on(this.projectId, this.handleAnalysesEvent.bind(this)),
        this.analysisGroupService.on(this.projectId, this.handleAnalysisGroupsEvent.bind(this)),
        this.predictiveService.on(this.projectId, this.handleDriverAnalysisEvent.bind(this)),
        this.interactiveSmartSummaryService.on(
          this.projectId,
          this.handleSmartSummaryEvent.bind(this)
        ),
        this.dashboardService.on(this.projectId, this.handleDashboardEvent.bind(this)),
      ];
    } catch (error) {
      this.logger.error('problem during realtime handlers set up', { error });
    }

    this.commentsHandlerService.showExpansionPanel$
      .pipe(
        tap((event) => {
          this.expansionPanelShowingOnWidget = event.widgetId;
          this.expansionPanelTypeOnWidget = event.panelType;
        }),
        takeUntil(this.unsub$)
      )
      .subscribe((event) => this.onExpansionPanelShown(true, event.widgetContainer));

    this.commentsHandlerService.hideExpansionPanel$
      .pipe(takeUntil(this.unsub$))
      .subscribe(() => (this.isExpansionPanelShowing = false));

    await this.refresh(aliasedDashboard);

    this.accountService.groupFieldsSub.takeUntil(this.unsub$).subscribe((accountMetadata) => {
      const customFieldsCalculated = accountMetadata.metadata.map((f) => ({
        fieldName: `metadata.${f.fieldName}`,
        fieldType: f.fieldType,
      }));
      this.widgetGroupStore.setCustomFields(customFieldsCalculated);
    });

    this.pinningService.data$.pipe(skip(1), takeUntil(this.unsub$)).subscribe((d) => {
      this.dashboard.payload.pins = d;
      this.dashboardStore.markDirty();
    });

    this.router.events.pipe(takeUntil(this.unsub$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const navParams = this.route.snapshot.paramMap;
        // handle when we've been routed directly from one dashboard to a different dashboard
        // such as "save as" on conflict
        if (navParams.get('dashboardId') !== this.dashboardId) {
          this.dashboardId = navParams.get('dashboardId');
          this.projectId = navParams.get('projectId');
          this.refreshDone = false;
          this.hasLoadedOnce = false;
          this.currentPageId = 0;
          this.refresh();
        }
      }
    });

    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.startDashboardUpdatePolling();
  }

  private startDashboardUpdatePolling(): void {
    if (this.dashboardUpdatePollingInterval) {
      clearInterval(this.dashboardUpdatePollingInterval);
    }
    this.dashboardUpdatePollingInterval = setInterval(() => {
      this.checkForDashboardUpdates();
    }, 30000);
  }

  private handleCollectionsEvent(data: RealtimeMessage<{ id: string; projectId: string }>) {
    if (data.verb === 'DELETE') {
      this.datasourceManagerStore.removeByCollection(data.object.id);
      this.setCanAddWidget();
    }
  }

  private async handleAnalysesEvent(
    data: RealtimeMessage<{ id: string; collectionId: string; projectId: string }>
  ) {
    this.logger.debug('handleAnalysesEvent', { data });
    if (data.verb === 'DELETE') {
      this.datasourceManagerStore.removeAnalysisByCollectionAndAnalysisId(
        data.object.collectionId,
        data.object.id
      );
      this.markWidgetsDirtyById(data.object.collectionId);
      this.setCanAddWidget();
    }
    this.rtrHandlerService.handleAnalysisRtrEvent(data).then(async (analysis) => {
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        const dataSource = await this.datasourceManagerStore.registerAnalysis(analysis);
        if (dataSource) {
          await this.setWidgetDataSources(dataSource);
        }
      }
      this.markWidgetsDirtyById(analysis.name);
      this.setCanAddWidget();
    });
  }

  private async handleAnalysisGroupsEvent(
    data: RealtimeMessage<{ id: string; projectId: string }>
  ) {
    this.logger.debug('handleAnalysisGroupsEvent', { data });
    if (data.verb === 'DELETE') {
      this.datasourceManagerStore.removeGroupById(data.object.id);
      this.markWidgetsDirtyById(data.object.id);
      this.setCanAddWidget();
    }
    this.rtrHandlerService.handleAnalysisGroupRtrEvent(data).then(async (group) => {
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        const dataSource = await this.datasourceManagerStore.registerGroup(group);
        await this.setWidgetDataSources(dataSource);
      }
      this.markWidgetsDirtyById(group.id);
      this.setCanAddWidget();
    });
  }

  private async handleDriverAnalysisEvent(
    data: RealtimeMessage<{ id: string; projectId: string; collectionId: string }>
  ) {
    this.logger.debug('handleDriverAnalysisEvent', { data });
    if (data.verb === 'DELETE') {
      this.datasourceManagerStore.removeDriverAnalysisByIdAndCollectionId(
        data.object.id,
        data.object.collectionId
      );
      this.setCanAddWidget();
      return;
    }
    this.rtrHandlerService.handleDriverAnalysisRtrEvent(data).then(async (driverAnalysis) => {
      if (data.verb === 'UPDATE' || data.verb === 'CREATE') {
        const dataSource = await this.datasourceManagerStore.registerKeyDriverAnalysis(
          driverAnalysis,
          driverAnalysis.collectionId
        );
        if (dataSource) {
          await this.setWidgetDataSources(dataSource);
        }
      }
      this.setCanAddWidget();
    });
  }

  private handleSmartSummaryEvent(data: RealtimeMessage<InteractiveSmartSummary>) {
    if (this.currentPageWidgets.some((w) => w.id === data.object.widgetId)) {
      return;
    }

    if (data.verb === 'UPDATE') {
      const pageTitle = this.dashboard.payload.pages.find(
        (p) =>
          p.id === this.dashboard.widgets.find((w) => w.id === data.object.widgetId).payload.page
      ).title;

      if (data.object.status === 'COMPLETED') {
        this.alertService.showToast(
          translate('widgets.interactive-smart-summary.summary-generating-finished-external', {
            pointName: data.object.payload.analyticItemName,
            pageTitle,
          })
        );
      }

      if (data.object.status === 'ERROR') {
        this.alertService.showErrorToast(
          translate('widgets.interactive-smart-summary.summary-generating-error-external', {
            pointName: data.object.payload.analyticItemName,
            pageTitle,
          })
        );
      }
    }
  }

  private updateCanAddPage(): void {
    if (!this.dashboard) {
      this.canAddPage.set(false);
      return;
    }
    this.canAddPage.set(this.dashboard.payload.pages.length < this.pageManager.maxPages);
  }

  /**
   * Handles real-time dashboard update events. When a permissioned user saves
   * changes to the dashboard while a read-only user is viewing it, show the
   * reload required modal.
   */
  private handleDashboardEvent(data: RealtimeMessage<Dashboard>) {
    this.logger.debug('handleDashboardEvent', { data });

    // Only handle updates to the current dashboard
    if (data.object.id !== this.dashboardId) {
      return;
    }

    // Only show reload modal for read-only users
    if (!this.isReadOnly) {
      return;
    }

    // Check if the dashboard revision has changed
    if (
      data.verb === 'UPDATE' &&
      data.object.revision !== undefined &&
      this.loadedDashboardRevision !== undefined &&
      data.object.revision > this.loadedDashboardRevision
    ) {
      this.showReloadRequiredModal = true;
    }
  }

  /**
   * Checks if the dashboard has been updated by another user.
   * Called when the page becomes visible (user returns to tab) or periodically.
   */
  private async checkForDashboardUpdates(): Promise<void> {
    if (!this.isReadOnly || !this.dashboardId || !this.projectId || this.showReloadRequiredModal) {
      return;
    }

    try {
      const latestDashboard = await this.dashboardService.getDashboardById(
        this.dashboardId,
        this.projectId
      );

      if (
        latestDashboard?.revision !== undefined &&
        this.loadedDashboardRevision !== undefined &&
        latestDashboard.revision > this.loadedDashboardRevision
      ) {
        this.showReloadRequiredPopup();
      }
    } catch (error) {
      this.logger.warn('Failed to check for dashboard updates', { error });
    }
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.checkForDashboardUpdates();
    }
  };

  private showReloadRequiredPopup(): void {
    if (this.showReloadRequiredModal) {
      return;
    }
    this.showReloadRequiredModal = true;

    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-reload-required-modal.title'),
        content: translate('dashboard-reload-required-modal.message'),
        confirmTitle: translate('dashboard-reload-required-modal.reload-button'),
        cancelTitle: '',
      })
      .afterClosed()
      .subscribe(() => {
        window.location.reload();
      });
  }

  ngOnDestroy(): void {
    if (this.wrapperResizeObserver) {
      this.wrapperResizeObserver.disconnect();
      this.wrapperResizeObserver = undefined;
    }
    this.changeDetectorRef.detach();
    this.authService.removeLogoutInterceptor(this);
    this.redrawIntervals.forEach((i) => clearInterval(i));
    this.redrawIntervals.clear();

    this.realtimeHandlers.forEach((handler) => {
      if (handler) {
        handler.disconnect();
      }
    });

    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.dashboardUpdatePollingInterval) {
      clearInterval(this.dashboardUpdatePollingInterval);
    }

    this.unsub$.next();
    this.unsub$.complete();

    this.timezoneService.timezone = this.preferences.timezone;
  }

  @DisableMethodTelemetry()
  private saveDashboardInCache(isUnsaved: boolean) {
    // FIXME: gets called every scroll event
    const cachedDashboard = this.dashboardService.loadCachedDashboard(
      this.dashboardId,
      this.projectId,
      this.session.info2.user_id
    );
    if (
      !Utils.deepEquals(this.dashboard, cachedDashboard, [
        'refresh',
        'minItemCols',
        'minItemRows',
        'dragEnabled',
        'resizeEnabled',
        'compactEnabled',
      ]) &&
      isUnsaved &&
      this.originalDashboard
    ) {
      this.dashboardService.storeCachedDashboard(this.dashboard, this.session.info2.user_id);
    }
    if (!isUnsaved) {
      this.dashboardService.removeCachedDashboard(
        this.dashboardId,
        this.projectId,
        this.session.info2.user_id
      );
    }
  }

  public elementFor(widgetId: string): WidgetComponent {
    const result = this.elementWidget.filter((x) => x.id === widgetId);
    if (result.length > 0) {
      return result[0];
    }
  }

  public componentFor(widgetId: string): AbstractWidgetComponent<any> {
    const result = this.elementFor(widgetId);
    if (result) {
      return result.getComponent();
    }
  }

  public asDatatable(widgetId: string, enabled: boolean): void {
    const component = this.componentFor(widgetId) as WidgetChartComponent;
    component.as_data_table = enabled;
  }

  public widgetGroups(): WidgetGroup[] {
    return this.widgetGroupStore.getByPage(this.currentPageId);
  }

  public getWidgetWidgetGroup(widgetId): WidgetGroup {
    return this.widgetGroupStore.getByWidget(widgetId);
  }

  public refreshWidgetGroups() {
    this.widgetGroupRefreshEmit.emit(true);
  }

  public async loadWidgetDataSource(widget: Widget) {
    if (!widget.dataSourceId && widget.payload.analysisName) {
      const ds = this.datasourceManagerStore.getById(widget.payload.analysisName);
      if (!ds) {
        this.logger.error(`Failed to find dataSource for analyis ${widget.payload.analysisName}`, {
          widget,
        });
        return false;
      }
      widget.dataSourceId = ds.id;
      return true;
    } else if (widget.dataSourceId) {
      const component = this.componentFor(widget.id);
      if (component?.needDataSource) {
        const ds = this.datasourceManagerStore.getById(widget.dataSourceId);
        if (!ds) {
          component.dataSourceNotFound = true;
          this.logger.error(`Failed to find dataSource for widget ${widget.dataSourceId}`, {
            widget,
          });
          return false;
        }
        widget.dataSourceId = ds.id;
        component.widgetDataSource = ds;
      }
      return true;
    }
    return false;
  }

  // loading stuff
  public async refresh(aliasedDashboard?: Dashboard): Promise<boolean> {
    this.isLoading.dashboard = true;
    this.changeDetectorRef.detach();
    const widgetsToAdd = [];
    try {
      const loadPromises: [Promise<void>, Promise<Session>, Promise<Dashboard>?] = [
        this.datasourceManagerStore.loadDataSources(this.projectId),
        this.authService.getSession(),
      ];

      if (!this.isDashboardNew && !aliasedDashboard) {
        loadPromises.push(this.dashboardService.getDashboardById(this.dashboardId, this.projectId));
      }
      const promises = await Promise.all(loadPromises);
      const [_, session] = promises;
      let dashboard = promises[2];

      this.session = session;
      dashboard ??= aliasedDashboard;
      if (!dashboard) {
        dashboard = this.dashboardService.loadCachedDashboard(
          this.dashboardId,
          this.projectId,
          session.info2.user_id
        );
      }
      if (!dashboard.widgetGroups) {
        dashboard.widgetGroups = {};
      }
      this.setCanAddWidget();
      if (this.hasLoadedOnce) {
        this.widgetGroupStore.loadWidgetGroups(dashboard);
      }

      this.dashboard = dashboard;
      this.loadedDashboardRevision = dashboard.revision;
      if (this.dashboard.payload.timezone) {
        this.timezoneService.timezone = this.dashboard.payload.timezone;
      }
      if (!this.dashboard.payload.notes) {
        this.dashboard.payload.notes = '';
      }
      this.setTimezoneData();

      if (this.dashboard.locked) {
        this.setWidgetGroupPanel.emit({
          dashboardIsLocked: this.dashboard.locked,
          sidePanelIsOpened: this.dashboard.payload.widgetGroupPanelIsOpened,
        });
      }

      if (this.dashboard.dashboard_version > 8) {
        this.comparisonService.comparisonListChanged$
          .pipe(takeUntil(this.unsub$))
          .subscribe((res) => (this.comparisonList = res));
      }

      this.originalTitle = this.dashboard.title;
      this.setTitle(
        translate('dashboard-view.label.dashboard-title', {
          dashboardName: this.originalTitle,
        })
      );
      this.isReadOnly = this.authService.isReadOnly;
      this.isAdmin = this.authService.isAccountAdmin;
      this.isSharedSession = this.authService.isSharedSession;
      this.isOwner = this.session.info2.user_id === this.dashboard.user_id;
      this.isExecutiveUser = this.authService.isExecutive;
      widgetsToAdd.splice(0, 1);
      this.refreshDone = true;
    } catch (err) {
      if (err.status === 401) {
        return false;
      }
      if (err.status === 404) {
        this.alertService.showErrorToast(
          translate('dashboard-view.error.load-fail', {
            dashboardName: this.dashboardId,
          }),
          err
        );
      } else {
        this.alertService.showErrorToast(
          translate('dashboard-view.error.dashboard-fail', {
            dashboardId: this.dashboardId,
          }),
          err
        );
      }
      const url = `groups/${this.groupService.getSelectedGroupId(true)}/projects/${this.projectId}`;
      this.router.navigate([url]);
      return false;
    }

    this.originalDashboard = Utils.deepCloneWithNulls(this.dashboard);

    this.dashboardName = this.dashboard.title;
    let loadedFromCache = false;
    try {
      if (!this.hasLoadedOnce) {
        this.originalDashboard.widgets.forEach((widget) => {
          if (widget.payload.dataSource) {
            widget.dataSourceId = widget.payload.dataSource.id;
            delete widget.payload.dataSource;
          }
        });

        const cachedDashboard = this.dashboardService.loadCachedDashboard(
          this.dashboardId,
          this.projectId,
          this.session.info2.user_id
        );
        if (cachedDashboard && !this.isReadOnly) {
          loadedFromCache = true;
          this.loadDashBoardCache(cachedDashboard);
        }
        this.update_last_accessed();

        this.syncComparisons();

        await this.migrateAndSave();

        this.widgetGroupStore.loadWidgetGroups(this.dashboard);

        this.dashboardPages = this.dashboard.payload.pages.map((p) => ({
          title: p.title,
          id: `${p.id}`,
        }));
        this.updateCanAddPage();
        this.onCurrentPageChanged(this.dashboard.payload.pages[0].id);

        //Setup the datasources and widget groups for the widgets
        for (const widget of this.currentPageWidgets) {
          this.widgetLoadingManager.onBeginLoading(widget.id);
          await this.loadWidgetDataSource(widget);
        }

        let firstTime = true;
        this.widgetLoadingManager.loading$
          .pipe(skip(1), takeUntil(this.unsub$))
          .subscribe((count) => {
            if (firstTime && count === 0) {
              if (!loadedFromCache) {
                this.originalDashboard = Utils.deepCloneWithNulls(this.dashboard);
                this.guardedIsUnsaved();
              }
              const loadTime = this.reportTiming('dashboard_loaded');
              this.logger.debug('Dashboard load complete: ' + loadTime);
              this.fullStoryService.setPageVars(PageNames.dashboard, {
                loadTime,
              });
              firstTime = false;
            }
          });
      }

      //Set the last payload id.
      let updatedWidgetDatasources = false;
      if (!this.dashboard.payload.lastDataSourceId || this.dashboard.payload.collectionName) {
        const list = this.datasourceManagerStore.getByCollectionName(
          this.dashboard.payload.collectionName
        );
        if (list) {
          const targetId = this.dashboard.payload.analysisName;
          const found = list.find((ds) => ds.analysisId === targetId);
          if (found) {
            this.dashboard.payload.lastDataSourceId = found.id;
            this.dashboard.payload.collectionName = '';
            this.dashboard.payload.analysisName = '';
            updatedWidgetDatasources = true;
          }
        }
      }
      if (updatedWidgetDatasources) {
        const d = await this.dashboardService.updateDashboard(this.dashboard);
        this.updateDateFields(d, this.dashboard);
        this.updateDateFields(d, this.originalDashboard);
      }
    } catch (err) {
      this.logger.error('error loading data sources', { err });
    }

    if (!this.skipPinningInit) {
      this.setPinning();
    }
    this.skipPinningInit = false;
    // Note: originalDashboard is re-baselined when widget loading settles
    // (see widgetLoadingManager.loading$ subscription above) so any final
    // render-time normalization is captured. Avoid re-cloning here, which
    // would race against that and resurrect the unsaved banner.

    this.isLoading.dashboard = false;
    this.hasLoadedOnce = true;
    this.inSaveFlow = false;
    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.reattach();
    }
    this.widgetsCountChanges.emit(this.dashboard);

    return true;
  }

  private syncComparisons() {
    if (this.dashboard.dashboard_version > 8) {
      const comparisonIds = new Set(
        this.dashboard.widgets
          .filter((w) => !!w.payload.comparison)
          .map((w) => w.payload.comparison)
      );

      for (const id of comparisonIds) {
        if (!this.dashboard.comparisons[id]) {
          this.logger.warn('Dangling comparisonID found', { id });
          this.dashboard.widgets
            .filter((w) => w.payload.comparison === id)
            .forEach((w) => delete w.payload.comparison);
        }
      }
    }
  }

  private async migrateAndSave() {
    const dashboardNeedsMigration = DashboardMigrationService.checkIfNeedsMigration(this.dashboard);
    const originalDashboardNeedsMigration =
      !!this.originalDashboard &&
      DashboardMigrationService.checkIfNeedsMigration(this.originalDashboard);
    if (dashboardNeedsMigration || originalDashboardNeedsMigration) {
      this.changeDetectorRef.detach();

      let hasChanges = true;
      try {
        hasChanges = this.isUnsaved;
      } catch (e) {
        this.logger.error('Failed to check unsaved', { e });
      }

      if (dashboardNeedsMigration) {
        DashboardMigrationService.migrateToCurrent(this.dashboard);
      }
      // When the user has pending changes we skip saveDashboard, so
      // originalDashboard is never re-baselined by it. Migrating it here
      // keeps the diff baseline in sync, preventing migration-only field
      // differences from polluting changedWidgets and pageChangeData.
      if (originalDashboardNeedsMigration) {
        DashboardMigrationService.migrateToCurrent(this.originalDashboard);
      }
      if (dashboardNeedsMigration && !hasChanges && !this.isDashboardNew && !this.isReadOnly) {
        await this.saveDashboard({ silent: true, isUserModification: false });
      }

      this.changeDetectorRef.reattach();
    }
  }

  // targetId can be an analysis id or a group id
  private markWidgetsDirtyById(targetId: string) {
    this.currentPageWidgets.forEach((widget) => {
      const ds = this.datasourceManagerStore.getById(widget.dataSourceId);
      if (
        !ds ||
        (ds.type === 'analysis' && ds.analysisId === targetId) ||
        (ds.type === 'group' && ds.groupId === targetId) ||
        (ds.type === 'group' &&
          ds.groupAnalyses.findIndex((analysisId) => analysisId === targetId) !== -1)
      ) {
        const component = this.componentFor(widget.id);
        component.isDirty = true;
      }
    });
  }

  public updateIsAnyWidgetDirty() {
    let dirty = false;
    this.currentPageWidgets.forEach((widget) => {
      const component = this.componentFor(widget.id);
      if (component && component.isDirty) {
        dirty = true;
      }
    });
    this.isAnyWidgetDirty = dirty;
  }

  public refreshDirtyWidgets() {
    this.currentPageWidgets.forEach((widget) => {
      const component = this.componentFor(widget.id);
      if (component.isDirty) {
        this.elementFor(widget.id).refreshWidget();
      }
    });
    this.updateIsAnyWidgetDataSourceRemoved();
  }

  updateIsAnyWidgetDataSourceRemoved() {
    let gotHit = false;
    this.currentPageWidgets.forEach((widget) => {
      const component = this.componentFor(widget.id);
      if (component && (component as any).dataSourceNotFound) {
        gotHit = true;
      }
    });
    this.isAnyWidgetDatasourceRemoved = gotHit;
    this.changeDetectorRef.detectChanges();
  }

  public refreshWidgets(widgets: string[], refreshAll = false) {
    this.currentPageWidgets.forEach((widget) => {
      if (widgets.includes(widget.id) || refreshAll) {
        this.elementFor(widget.id)?.refreshWidget();
      }
    });
    this.updateIsAnyWidgetDirty();
  }

  public updateWidgetHasWidgetGroups() {
    this.currentPageWidgets.forEach((widget) => {
      this.elementFor(widget.id)?.updateWidgetGroupsExist();
      this.elementFor(widget.id)?.updateWidgetGroupsParentCheck();
    });
  }

  public async setWidgetDataSources(targetDataSource?: WidgetDataSource) {
    for (const widget of this.currentPageWidgets) {
      const key = widget.dataSourceId ? widget.dataSourceId : DATASOURCE_GROUP_KEY;
      const dataSource = this.datasourceManagerStore.getById(key);
      if (dataSource && (!targetDataSource || targetDataSource.id === dataSource.id)) {
        widget.dataSourceId = dataSource.id;
        if (targetDataSource) {
          const component = this.componentFor(widget.id);
          const element = this.elementFor(widget.id);
          if (element.isDocumentList()) {
            await (component as WidgetDocumentListComponent).reloadDisplayColumns(dataSource);
          }
        }
      }
    }
    this.updateIsAnyWidgetDirty();
    this.updateIsAnyWidgetDataSourceRemoved();
  }

  public reload(): void {
    for (const widget of this.currentPageWidgets) {
      const widgetElement = this.elementFor(widget.id);
      // Safely skip widgets whose host elements are not yet mounted (e.g. right after applying a Saved View).
      if (!widgetElement) {
        continue;
      }

      const widgetComponent = widgetElement.getComponent();
      widgetElement.widgetGroup = this.widgetGroupStore.getByWidget(widget.id);
      widgetComponent.widgetGroup = this.widgetGroupStore.getByWidget(widget.id);
      widgetComponent.refresh(true, true);
    }
    this.updateIsAnyWidgetDirty();
    this.updateIsAnyWidgetDataSourceRemoved();
  }

  public redraw(
    widgetsToRedraw: GridsterItem[] = this.currentPageWidgets,
    redrawActions = false
  ): void {
    this.changeDetectorRef.detectChanges();

    for (const widget of widgetsToRedraw) {
      ((item) => {
        const redrawInterval = setInterval(() => {
          const widgetElement = this.elementFor(item.id);
          if (widgetElement) {
            this.redrawIntervals.delete(redrawInterval);
            clearInterval(redrawInterval);
            widgetElement.getComponent().redraw();
            if (redrawActions) {
              widgetElement.pillWrapper.checkCompactSize();
            }
          }
        }, 50);
        this.redrawIntervals.add(redrawInterval);
      })(widget);
    }
    this.updateIsAnyWidgetDirty();
    this.updateIsAnyWidgetDataSourceRemoved();
  }

  public async forceRedraw(func?: () => void, funcAfter?: () => void): Promise<void> {
    if (this.dashboard) {
      this.changeDetectorRef.detach();

      if (func) {
        func();
      }

      this.changeDetectorRef.detectChanges();
      this.changeDetectorRef.reattach();

      setTimeout(() => {
        this.currentPageWidgetsShowing = true;
        this.redraw();

        if (funcAfter) {
          funcAfter();
        }
      }, 50);

      this.widgetsCountChanges.emit(this.dashboard);
    }
  }

  /**
   * Reflow widgets in row-major (left-to-right, top-to-bottom) order.
   * Supports mixed-width widgets by wrapping when accumulated cols exceed DASHBOARD_WIDTH.
   * If an explicit order is provided, it is used. Otherwise, it prefers the saved
   * per-page order, falling back to current visual order.
   */
  private reflowRowMajor(orderIds?: string[]): void {
    const widgets = this.currentPageWidgets;
    if (!widgets?.length) {
      return;
    }

    // Build stable order: explicit order if provided (e.g., pre-delete),
    // otherwise use saved per-page order if available; fallback to current visual order
    const baseOrderIds = orderIds?.length ? orderIds : this.getSavedOrderForCurrentPage();
    const ordered = baseOrderIds?.length
      ? baseOrderIds.flatMap((id) => {
          const w = widgets.find((it) => it.id === id);
          return w ? [w] : [];
        })
      : [...widgets].sort((a, b) => a.y - b.y || a.x - b.x);

    const applyPositions = () => {
      // Temporarily disable compaction and pushing to prevent Gridster from reshuffling while we assign positions
      const prevCompact = this.dashboardOptions.compactType;
      const prevPushItems = this.dashboardOptions.pushItems;
      const prevPushResizeItems = this.dashboardOptions.pushResizeItems;
      this.dashboardOptions.compactType = CompactType.None;
      this.dashboardOptions.pushItems = false;
      this.dashboardOptions.pushResizeItems = false;
      if (this.dashboardOptions?.api?.optionsChanged) {
        this.dashboardOptions.api.optionsChanged();
      }

      let cursorX = 0;
      let cursorY = 0;
      let rowHeight = 1;

      for (const w of ordered) {
        const cols = Math.max(1, Math.min(w.cols || 1, DASHBOARD_WIDTH));
        const rows = Math.max(1, w.rows || 1);

        if (cursorX + cols > DASHBOARD_WIDTH) {
          // wrap to next row
          cursorY += rowHeight;
          cursorX = 0;
          rowHeight = 1;
        }

        w.x = cursorX;
        w.y = cursorY;

        cursorX += cols;
        if (rows > rowHeight) {
          rowHeight = rows;
        }
      }

      // Ensure DOM iteration order matches our computed row-major order
      this.currentPageWidgets = [...ordered];

      // Run a compaction pass to close vertical gaps while preserving horizontal order (x)
      this.dashboardOptions.compactType = CompactType.CompactUp;
      this.dashboardOptions.pushItems = true;
      this.dashboardOptions.pushResizeItems = true;
      if (this.dashboardOptions?.api?.optionsChanged) {
        this.dashboardOptions.api.optionsChanged();
      }

      // Persist order and restore previous compaction/push settings and apply changes
      this.saveOrderForCurrentPage(ordered.map((w) => w.id));
      this.dashboardOptions.compactType = prevCompact;
      this.dashboardOptions.pushItems = prevPushItems;
      this.dashboardOptions.pushResizeItems = prevPushResizeItems;
      if (this.dashboardOptions?.api?.optionsChanged) {
        this.dashboardOptions.api.optionsChanged();
      }
    };

    // Apply now and once more shortly after to override any late compaction.
    // Gridster may adjust positions after optionsChanged/compaction settles,
    // so we re-apply once to ensure our row-major positions persist.
    applyPositions();
    setTimeout(() => applyPositions(), this.REFLOW_REAPPLY_DELAY_MS);
  }

  // dashboard changes
  public async saveDashboard({
    silent,
    skipNavigation,
    isUserModification = true,
  }: {
    silent?: boolean;
    skipNavigation?: boolean;
    isUserModification?: boolean;
  } = {}): Promise<Dashboard> {
    if (this.authService.isDemoMode) {
      this.dialogService.showDemoModeDialog();
      return;
    }

    if (this.dashboard.locked) {
      return;
    }

    // on saving, any zoomed date ranges should be made permanent
    // we'll keep a list to reload and do that after the save is complete
    const widgetsNeedingReload = this.dashboard.widgets
      .map((widget) => {
        if (widget.payload.zoomDateFilter) {
          delete widget.payload.zoomDateFilter;
          return widget.id;
        }
      })
      .filter((widgetId) => widgetId);

    try {
      this.inSaveFlow = true;

      if (this.isDashboardNew) {
        await this.createDashboard(skipNavigation);
        return;
      }
      const d = await this.updateDashboard(this.dashboard, silent, isUserModification);
      if (!d) {
        return; // cancelled
      }
    } catch (error) {
      this.inSaveFlow = false;
      this.alertService.showErrorToast(
        translate('dashboard-view.alert.save-fail', {
          dashboardName: this.dashboard.title,
        }),
        error
      );
      return;
    }

    widgetsNeedingReload.forEach((widgetId) => {
      const widgetComponent = this.elementFor(widgetId);
      if (widgetComponent) {
        widgetComponent.refreshWidget();
      }
    });

    this.originalDashboard = Utils.deepCloneWithNulls(this.dashboard);
    this.dashboardService.removeCachedDashboard(
      this.dashboardId,
      this.projectId,
      this.session.info2.user_id
    );
    this.dashboardStore.reset();
    this.projectService
      .touchProject(this.projectId)
      .then(() => this.logger.debug('Touched project'));
    this.inSaveFlow = false;
    return this.dashboard;
  }

  public async createDashboard(skipNavigation?) {
    const savedDashboard = await this.dashboardService.createDashboard(
      this.dashboard,
      this.projectId
    );
    this.dashboardService.removeCachedDashboard(
      this.dashboardId,
      this.projectId,
      this.session.info2.user_id
    );
    this.dashboardStore.reset();

    // Update feed dashboardIds from "new" to the real dashboard ID
    // Collect feed IDs from widget payloads to avoid race conditions with other unsaved dashboards
    try {
      // Collect all feed IDs from Insight Feed widgets
      const feedIds = this.dashboard.widgets
        .filter(
          (widget) =>
            widget.payload.type === 'insightFeed' && widget.payload.feedSource === 'dashboardFeeds'
        )
        .flatMap((widget) => {
          const feedWidgetComponent = this.elementFor(widget.id);
          return feedWidgetComponent.insightFeeds
            .filter((feed) => feed.dashboardId === 'new')
            .map((feed) => feed.id);
        });

      if (feedIds.length) {
        await this.aiInsightsGroupFeedService.updateFeedsDashboardId(feedIds, savedDashboard.id);
      }
    } catch (error) {
      this.logger.error('Failed to update feed dashboard IDs after dashboard creation', { error });
    }

    this.alertService.showToast(
      translate('dashboard-view.alert.title-change', {
        dashboardName: this.originalTitle,
      })
    );

    if (!skipNavigation) {
      this.router.navigate([
        'groups',
        this.groupService.getSelectedGroupId(true),
        'projects',
        this.projectId,
        'dashboards',
        savedDashboard.id,
      ]);
    }
  }

  public async updateDashboard(dashboard, silent?, isUserModification = true): Promise<Dashboard> {
    let d: Dashboard;
    try {
      d = await this.dashboardService.updateDashboard(dashboard, true, isUserModification);
    } catch (ex) {
      if (ex.status === 409) {
        // conflict found
      } else {
        throw ex;
      }
    }
    if (!d) {
      d = await this.openConflictDialogAndAwaitResult(dashboard);
    }
    if (!d) {
      return;
    }
    this.updateDateFields(d, this.dashboard);
    this.updateDateFields(d, this.originalDashboard);
    this.originalTitle = d.title;
    this.setTitle(
      translate('dashboard-view.label.dashboard-title', {
        dashboardName: this.originalTitle,
      })
    );
    if (!silent) {
      this.alertService.showToast(
        translate('dashboard-view.alert.title-change', {
          dashboardName: this.originalTitle,
        })
      );
    }
    return d;
  }

  async openConflictDialogAndAwaitResult(dashboard: Dashboard): Promise<Dashboard | undefined> {
    return new Promise((resolve) => {
      let saveAsCopy = false;
      this.dialogService
        .showSimpleDialog({
          title: translate('dashboard-view.conflict-dialog.title'),
          content: translate('dashboard-view.conflict-dialog.content'),
          confirmTitle: translate('dashboard-view.conflict-dialog.confirm-title'),
          confirmColor: SimpleDialogButtonColor.green,
          cancelTitle: translate('dashboard-view.conflict-dialog.cancel-title'),
          buttons: [
            {
              title: translate('dashboard-view.conflict-dialog.save-as-title'),
              color: SimpleDialogButtonColor.orange,
              handler: (dialogRef) => {
                dialogRef.close();
                this.saveAsDashboard(true);
                saveAsCopy = true;
              },
            },
          ],
        })
        .afterClosed()
        .subscribe(async (result) => {
          if (result) {
            const d = await this.dashboardService.updateDashboard(dashboard, false);
            resolve(d);
          } else if (saveAsCopy) {
            // save as copy already happened, just need to bail out
            resolve(undefined);
          } else {
            // save was cancelled
            resolve(undefined);
          }
        });
    });
  }

  revertDashboard() {
    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-view.label.revert'),
        content: translate('dashboard-view.text.revert'),
        confirmTitle: translate('dashboard-view.label.confirm-revert'),
        cancelTitle: translate('dashboard-view.label.cancel'),
        confirmColor: SimpleDialogButtonColor.green,
      })
      .afterClosed()
      .subscribe((res) => {
        if (res) {
          this.revertDashboardAction();
        }
      });
  }

  revertDashboardAction() {
    this.dashboard = Utils.deepCloneWithNulls(this.originalDashboard);
    this.timezoneService.timezone = this.dashboard.payload.timezone ?? this.preferences.timezone;
    this.setTimezoneData(this.timezoneService.timezone, this.timezoneService.timezoneOffset);

    this.dashboardName = this.dashboard.title;
    if (!this.currentPage) {
      this.onCurrentPageChanged(this.dashboard.payload.pages[0].id);
    } else {
      this.currentPageWidgets = this.dashboard.widgets.filter(
        (w) => w.payload.page === this.currentPageId
      );
    }
    this.arePageWidgetDetailsHidden = this.currentPage.widgetDetailsHidden;
    this.setCanAddWidget();
    this.chartCustomColorService.resetColorDictionary();
    this.dashboardService.removeCachedDashboard(
      this.dashboardId,
      this.projectId,
      this.session.info2.user_id
    );
    this.widgetGroupStore.loadWidgetGroups(this.dashboard);
    this.widgetGroupRefreshEmit.emit(true);
    this.comparisonService.loadComparisons(this.dashboard.comparisons, this.currentPageId);
    this.actionPlanSidePaneService.load(this.dashboard.payload.actionPlans, this.currentPageId);
    this.datasourceManagerStore.clearDataSourceWidgetMap();
    this.setPinning();
    this.onValidateDashboardName();
    setTimeout(() => {
      this.updateWidgetHasWidgetGroups();
    }, 50);
    this.dashboardStore.reset();
  }

  cancelNewDashboard() {
    this.router.navigate(
      ['groups', this.groupService.getSelectedGroupId(true), 'projects', this.projectId],
      {
        state: {
          hideSave: true,
        },
      }
    );
  }

  public onDashboardOptionsClosed(): void {
    this.showDashboardOptions = false;
  }

  public async onDashboardOptionsSave(event: SaveDashboardEvent) {
    try {
      this.isLoading.dashboard = true;

      const eventPayload = event.dashboard.payload;
      const dashboardPayload = this.dashboard.payload;
      const originalDashboardPayload = this.originalDashboard.payload;
      const visibilityChanged = this.dashboard.visibility !== event.dashboard.visibility;
      const originalWidgetGroup = getDashboardLevelWidgetGroup(this.dashboard);
      const newWidgetGroup = getDashboardLevelWidgetGroup(event.dashboard);
      const widgetGroupChanged =
        (originalWidgetGroup !== undefined && newWidgetGroup === undefined) ||
        (originalWidgetGroup === undefined && newWidgetGroup !== undefined);

      dashboardPayload.notes = eventPayload.notes;
      dashboardPayload.isExecutive = eventPayload.isExecutive;
      this.dashboard.title = event.dashboard.title;
      this.dashboard.visibility = event.dashboard.visibility;

      //Based on DASH-4965 changes of title, notes or isExecutive are not revertable (update in background)
      //Other changes affecting widgets are revertable
      originalDashboardPayload.notes = eventPayload.notes;
      originalDashboardPayload.isExecutive = eventPayload.isExecutive;
      this.originalDashboard.title = event.dashboard.title;
      this.originalDashboard.visibility = event.dashboard.visibility;

      if (widgetGroupChanged) {
        if (newWidgetGroup) {
          this.widgetGroupStore.registerWidgetGroup(newWidgetGroup);
          this.dashboard.widgetGroups[newWidgetGroup.id] = newWidgetGroup;
          this.originalDashboard.widgetGroups[newWidgetGroup.id] =
            Utils.deepCloneWithNulls(newWidgetGroup);
        } else {
          delete this.dashboard.widgetGroups[originalWidgetGroup.id];
          delete this.originalDashboard.widgetGroups[originalWidgetGroup.id];
          this.widgetGroupStore.unregisterWidgetGroup(originalWidgetGroup);

          if (this.dashboard.payload.type === 'predictive') {
            this.currentPageWidgets.forEach((widget) => {
              this.elementFor(widget.id).updateTemplateBooleans();
            });
          }

          this.refreshWidgets(originalWidgetGroup.widgets);
        }
        this.updateWidgetHasWidgetGroups();
        this.widgetGroupRefreshEmit.emit(true);
      }

      const lockedChanged = this.dashboard.locked !== event.dashboard.locked;
      let needReload = lockedChanged;

      if (lockedChanged) {
        let lockUnlockPromise;
        if (event.dashboard.locked) {
          this.dashboard.payload.widgetGroupPanelIsOpened =
            this.localStorageService.getItem(LocalStorageKey.WIDGET_GROUP_PANEL_OPEN) ?? true;
          originalDashboardPayload.widgetGroupPanelIsOpened =
            this.localStorageService.getItem(LocalStorageKey.WIDGET_GROUP_PANEL_OPEN) ?? true;

          if (this.isUnsaved) {
            const response = await this.saveDashboard({ silent: true });
            this.updateDateFields(response, this.dashboard);
            this.updateDateFields(response, this.originalDashboard);
            this.updateDateFields(response, event.dashboard);
          }

          lockUnlockPromise = this.dashboardService.lockDashboard(this.dashboard);
        } else {
          lockUnlockPromise = this.dashboardService.unlockDashboard(this.dashboard);
          this.dashboard.payload.widgetGroupPanelIsOpened = undefined;
          originalDashboardPayload.widgetGroupPanelIsOpened = undefined;
        }

        const response = await lockUnlockPromise;
        this.dashboard.locked = response.locked;
        this.updateDateFields(response, this.dashboard);
        this.originalDashboard.locked = response.locked;
        this.updateDateFields(response, this.originalDashboard);

        this.elementWidget.forEach((w) => {
          if (w.isDocumentList()) {
            w.redrawWidget();
          }
        });
      } else {
        if (visibilityChanged) {
          this.alertService.showToast(
            this.dashboard.visibility === 'UNLISTED'
              ? translate('dashboard-view.alert.set-hidden')
              : translate('dashboard-view.alert.set-public')
          );
        }

        const response = await this.updateDashboard(this.originalDashboard, true);
        if (!response) {
          return;
        }
        this.updateDateFields(response, this.dashboard);
        this.updateDateFields(response, this.originalDashboard);
        this.updateDateFields(response, event.dashboard);
      }

      this.setWidgetGroupPanel.emit({
        dashboardIsLocked: this.dashboard.locked,
        sidePanelIsOpened: this.dashboard.payload.widgetGroupPanelIsOpened,
      });

      if (
        eventPayload.collectionName !== dashboardPayload.collectionName ||
        eventPayload.analysisName !== dashboardPayload.analysisName ||
        eventPayload.sentimentScale !== dashboardPayload.sentimentScale ||
        eventPayload.sentimentDisplay !== dashboardPayload.sentimentDisplay
      ) {
        for (const widget of this.dashboard.widgets) {
          const widgetPayload = widget.payload;

          let analyticItem;
          let widgetFilterSet;

          if (widgetPayload.analyticItem && widgetPayload.filters) {
            analyticItem = `analyses.${widgetPayload.analyticItem}.sentimentPolarity`;
            widgetFilterSet = widgetPayload.filters[widgetPayload.analyticItem] as FilterSet;
          }

          let documentItem;
          let documentFilterSet;

          if (widgetPayload.filters) {
            documentItem = 'analyses.sentimentPolarity';
            documentFilterSet = widgetPayload.filters.analyses as FilterSet;
          }
          const legends = Object.assign({}, widgetPayload.legends);
          if (dashboardPayload.sentimentScale === 3 && eventPayload.sentimentScale === 5) {
            // Change from 3 -> 5 point
            FilterService.transformAnalyticFeaturePolarityFilter5(widgetFilterSet, analyticItem);
            FilterService.transformDocumentPolarityFilter5(documentFilterSet, documentItem);

            if (widgetPayload.type === 'sentimentPolarity') {
              legends['Very Positive'] = widgetPayload.legends['Positive'];
              legends['Somewhat Positive'] = widgetPayload.legends['Positive'];
              legends['Neutral'] = widgetPayload.legends['Neutral'];
              legends['Somewhat Negative'] = widgetPayload.legends['Negative'];
              legends['Very Negative'] = widgetPayload.legends['Negative'];
            }

            if (widgetPayload.type === 'grid') {
              widgetPayload.numericMetadataFields
                .filter((field) => field.percentageSentiment)
                .forEach((field) => {
                  if (field.percentageSentiment === 'positive') {
                    field.percentageSentiment = 'very positive';
                  }

                  if (field.percentageSentiment === 'negative') {
                    field.percentageSentiment = 'very negative';
                  }
                });
            }
          } else if (dashboardPayload.sentimentScale === 5 && eventPayload.sentimentScale === 3) {
            // Change from 5 -> 3 point
            FilterService.transformAnalyticFeaturePolarityFilter3(widgetFilterSet, analyticItem);
            FilterService.transformDocumentPolarityFilter3(documentFilterSet, documentItem);

            if (widgetPayload.type === 'sentimentPolarity') {
              legends['Positive'] = false;
              if (
                widgetPayload.legends['Very Positive'] ||
                widgetPayload.legends['Somewhat Positive']
              ) {
                legends['Positive'] = true;
              }

              legends['Neutral'] = widgetPayload.legends['Neutral'];

              legends['Negative'] = false;
              if (
                widgetPayload.legends['Very Negative'] ||
                widgetPayload.legends['Somewhat Negative']
              ) {
                legends['Negative'] = true;
              }
            }

            if (widgetPayload.type === 'grid') {
              widgetPayload.numericMetadataFields
                .filter((field) => field.percentageSentiment)
                .forEach((field) => {
                  if (field.percentageSentiment.includes('positive')) {
                    field.percentageSentiment = 'positive';
                  }

                  if (field.percentageSentiment.includes('negative')) {
                    field.percentageSentiment = 'negative';
                  }
                });
            }
          }

          widgetPayload.sentimentScale = eventPayload.sentimentScale;
          widgetPayload.sentimentDisplay = eventPayload.sentimentDisplay;
          widgetPayload.legends = legends;
        }

        dashboardPayload.collectionName = eventPayload.collectionName;
        dashboardPayload.analysisName = eventPayload.analysisName;
        dashboardPayload.sentimentScale = eventPayload.sentimentScale;
        dashboardPayload.sentimentDisplay = eventPayload.sentimentDisplay;

        needReload = true;
      }
      this.dashboardStore.markDirty();

      this.isLoading.dashboard = false;
      if (needReload) {
        this.reload();
      }
    } catch (error) {
      this.alertService.showErrorToast(
        translate('dashboard-view.alert.save-fail', {
          dashboardName: event.dashboard.title,
        }),
        error
      );
    }
    event.done(null);
  }

  public async saveAsDashboard(revert = false) {
    this.revertSaveAsDashboard = revert;
    if (this.authService.isDemoMode) {
      this.dialogService.showDemoModeDialog();
      return;
    }
    this.newDashboardModalData = {
      project: this.project,
      timezone: null,
      timezoneOffset: null,
      saveAsDashboard: this.dashboard,
      title: translate('dashboard-view.label.save-dashboard-as'),
    };
    this.showNewDashboardModal = true;
  }

  public onNewDashboardModalClosed(result: Dashboard) {
    this.showNewDashboardModal = false;
    if (result) {
      this.alertService.showToast(
        translate('dashboard-view.alert.dashboard-saved-as', {
          dashboardName: result.title,
        })
      );
      this.inSaveFlow = true;
      this.googleAnalyticsService.sendEvent('create_dashboard', 'Projects');
      this.dashboardService.removeCachedDashboard(
        this.dashboardId,
        this.projectId,
        this.session.info2.user_id
      );
      this.projectService
        .touchProject(this.projectId)
        .then(() => this.logger.debug('Touched project'));
      if (this.revertSaveAsDashboard) {
        const url = `/groups/${this.groupService.getSelectedGroupId(true)}/projects/${
          this.projectId
        }/dashboards/${result.id}`;
        this.router.navigateByUrl(url);
      }
    }
    this.revertSaveAsDashboard = false;
  }

  public shareDashboard() {
    this.dialogService.showDialog<ShareDashboardDialogComponent, ShareDashboardDialogOptions>(
      ShareDashboardDialogComponent,
      {
        dashboard: this.dashboard,
      },
      {},
      false
    );
  }

  buildAIContext() {
    return {
      dashboard: this.dashboard,
    };
  }

  buildAITools(): AITool[] {
    return [
      //Exports
      {
        name: 'export_dashboard',
        label: this.translocoService.translate('ai-assistant.tool.export-dashboard'),
        description: 'Export the current dashboard configuration.',
        callback: () => {
          try {
            this.dashboardService.exportDashboard(this.dashboard);
            return { exportStatus: 'done' };
          } catch (e) {
            return { exportStatus: 'failed' };
          }
        },
      },
      {
        name: 'download_dashboard_page_powerpoint',
        label: this.translocoService.translate(
          'ai-assistant.tool.download-dashboard-page-powerpoint'
        ),
        description: 'Download the current dashboard given page in PowerPoint format.',
        properties: [
          {
            type: 'number',
            name: 'pageNumber',
            description:
              'The integer value of the dashboard page the user wants to download. (Default value: 1)',
            required: true,
          },
        ],
        callback: (args) => {
          try {
            const input = args.pageNumber;
            this.downloadDashboardPage(typeof input === 'number' ? input : parseInt(input, 10));
            return { downloadStatus: 'done' };
          } catch (e) {
            return { downloadStatus: 'failed' };
          }
        },
      },
      //Page actions
      {
        name: 'clone_current_page',
        label: this.translocoService.translate('ai-assistant.tool.clone-current-page'),
        description: 'Clone the current page of the current dashboard.',
        callback: () => {
          try {
            this.clonePage();
            return { cloneStatus: 'done' };
          } catch (e) {
            return { cloneStatus: 'failed' };
          }
        },
      },
      //Widget actions
      {
        name: 'clone_widget_by_id',
        label: this.translocoService.translate('ai-assistant.tool.clone-widget-by-id'),
        description: 'Use it only when the user want to clone a widget.',
        properties: [
          {
            type: 'string',
            name: 'widgetId',
            description: 'The unique ID of the widget to clone.',
            required: true,
          },
        ],
        callback: (args) => {
          try {
            const widget = this.dashboard.widgets.filter((w) => w.id === args.widgetId)[0];
            this.onCloneWidget(widget);
            return { cloneStatus: 'done' };
          } catch (e) {
            return { cloneStatus: 'failed' };
          }
        },
      },
    ];
  }

  public async exportDashboard() {
    if (this.isUnsaved) {
      await this.saveDashboard();
    }

    this.dashboardService.exportDashboard(this.dashboard);
  }

  restoreDashboard($event) {
    this.originalDashboard = $event;
    this.revertDashboardAction();
  }

  public setSavedViewBaseline(savedView: SavedView | null | undefined): void {
    if (!savedView || !this.dashboard) {
      this.savedViewBaselineHash = undefined;
      return;
    }

    this.savedViewBaselineHash = this.buildSavedViewHash();
  }

  public applySavedView(savedView: SavedView): void {
    if (!this.dashboard || !savedView?.widgets) {
      return;
    }

    this.dashboard.widgets = Utils.deepCloneWithNulls(savedView.widgets);
    this.dashboard.widgetGroups = Utils.deepCloneWithNulls(savedView.widgetGroups ?? {});

    this.currentPageWidgets = this.dashboard.widgets.filter(
      (w) => w.payload.page === this.currentPageId
    );

    this.widgetGroupStore.loadWidgetGroups(this.dashboard);
    this.widgetGroupRefreshEmit.emit(true);

    this.updateWidgetHasWidgetGroups();
    this.reload();

    this.setSavedViewBaseline(savedView);
  }

  public loadDashBoardCache(cachedDashboard: Dashboard) {
    const tempLastAccessedDate = this.dashboard.last_accessed_date;
    this.dashboard = cachedDashboard;
    this.dashboardStore.markDirty();
    this.timezoneService.timezone = this.dashboard.payload.timezone ?? this.preferences.timezone;
    this.setTimezoneData(this.timezoneService.timezone, this.timezoneService.timezoneOffset);
    this.dashboard.last_accessed_date = tempLastAccessedDate;
    this.dashboardService.removeCachedDashboard(
      this.dashboardId,
      this.projectId,
      this.session.info2.user_id
    );
  }

  public async downloadDashboardPage(pageId: number) {
    if (this.isUnsaved) {
      await this.saveDashboard();
    }

    const pptx = this.pptxService.createPptx();
    const tableWidth = 12; // in inches

    const rows = [
      [{ text: translate('dashboard-view.label.dashboard-name') }, { text: this.dashboard.title }],
      [{ text: translate('dashboard-view.label.page-name') }, { text: this.currentPage.title }],
      [{ text: translate('dashboard-view.label.generated') }, { text: moment().format('LLL') }],
    ];
    pptx.addTableSlide(rows, {
      autoPageLineWeight: -0.3,
      w: tableWidth,
      border: {
        color: 'black',
        pt: 2,
        type: 'solid',
      },
    });

    const widgetsOnPage = this.dashboard.widgets.filter((w) => w.payload.page === pageId);

    for (const widget of widgetsOnPage) {
      const element = this.elementFor(widget.id);
      const widgetComponent = element.getComponent();

      if (element.isWidgetDatasourceRemoved()) {
        this.alertService.showToast(
          translate('dashboard-view.alert.data-source-removed', {
            widgetName: widget.title || widgetComponent.defaultTitle,
          })
        );
        element.actionsWrapper.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });

        return;
      }

      await widgetComponent.exportAsPptx(pptx);
    }

    pptx.save(
      translate('dashboard-view.label.export-name', {
        pageName: this.currentPage.title,
      })
    );
  }

  public async deleteDashboard(): Promise<void> {
    if (this.authService.isDemoMode) {
      this.dialogService.showDemoModeDialog();
      return;
    }

    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-view.label.delete-dashboard-dialog', {
          dashboardName: Utils.truncate(this.dashboard.title, 30),
        }),
        titleTooltip:
          this.dashboard.title.length > 30
            ? translate('dashboard-view.label.delete-dashboard-dialog', {
                name: this.dashboard.title,
              })
            : '',
        content: translate('dashboard-view.text.delete-dashboard-dialog'),
        confirmTitle: translate('dashboard-view.label.confirm-delete-dashboard-dialog'),
        cancelTitle: translate('dashboard-view.label.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          this.changeDetectorRef.detach();
          this.dashboard = Utils.deepCloneWithNulls(this.originalDashboard);
          this.dashboardService.removeCachedDashboard(
            this.dashboardId,
            this.projectId,
            this.session.info2.user_id
          );
          this.projectService
            .touchProject(this.dashboard.project_id)
            .then(() => this.logger.debug('Touched project'));

          this.alertService.showToast(
            translate('dashboard-view.alert.deleting-dashboard', {
              dashboardName: this.dashboard.title,
            })
          );
          await this.dashboardService.deleteDashboard(this.dashboard.id, this.projectId);

          this.router.navigate(
            [`groups/${this.groupService.getSelectedGroupId(true)}/projects`, this.projectId],
            { replaceUrl: true }
          );
        }
      });
  }

  getAvailableWidgets(): Widget[] {
    let availableWidgets = [];

    switch (this.selectModeStore.mode()) {
      case 'edit':
        return availableWidgets;
      case 'filters':
        availableWidgets.push(this.selectModeStore.selected()[0]);
        availableWidgets.push(
          ...this.currentPageWidgets.filter((widget) => {
            return (
              widget.id !== availableWidgets[0].id &&
              widget.component !== 'DashboardWidgetMarkdownComponent' &&
              widget.component !== 'DashboardWidgetNotesComponent' &&
              !widget.payload.isSpecial &&
              widget.component !== 'DashboardWidgetPredictiveComponent' &&
              widget.payload.type !== 'journey'
            );
          })
        );

        return availableWidgets;
      case 'widgetGroup':
      case 'widgetGroupBulkEdit':
        const usedWidgets = this.widgetGroupStore.widgetsInUse(
          this.selectModeStore.data().widgetGroup?.id || ''
        );

        availableWidgets = this.currentPageWidgets.filter(
          (widget) =>
            !usedWidgets.includes(widget.id) &&
            widget.component !== 'DashboardWidgetMarkdownComponent' &&
            widget.component !== 'DashboardWidgetNotesComponent' &&
            !hasSectionFilters(widget) &&
            widget.payload.type !== 'journey'
        );

        if (!this.selectModeStore.data().widgetGroup?.isImpact) {
          availableWidgets = availableWidgets.filter((widget) => {
            return (
              !widget.payload.isSpecial && widget.component !== 'DashboardWidgetPredictiveComponent'
            );
          });
        }

        return availableWidgets;
      case 'comparison':
        for (const widget of this.currentPageWidgets) {
          const element = this.elementFor(widget.id);
          if (
            (!element.comparison ||
              element.comparison.id === this.selectModeStore.data().comparison.id) &&
            element?.canApplyComparison(this.selectModeStore.data().comparison)
          ) {
            availableWidgets.push(widget);
          }
        }

        return availableWidgets;
      case 'analyticItem':
        availableWidgets = this.currentPageWidgets.filter((w) =>
          ActionPlanConstants.SELECTABLE_RATING_WIDGET_TYPES.includes(w.payload.type)
        );

        return availableWidgets;
      case 'goal':
        for (const widget of this.currentPageWidgets) {
          const element = this.elementFor(widget.id);
          if (element.currentReport?.canSetChartGoal()) {
            availableWidgets.push(widget);
          }
        }

        return availableWidgets;
      case 'dateMarker':
        for (const widget of this.currentPageWidgets) {
          const element = this.elementFor(widget.id);
          const chartPayload = element.widget.payload as WidgetChartOptions;
          if (
            element.currentReport?.canSetDateMarkers() &&
            chartPayload &&
            (!chartPayload.dateMarkers || chartPayload.dateMarkers.length < MAX_DATE_MARKER)
          ) {
            availableWidgets.push(widget);
          }
        }

        return availableWidgets;
      case 'weight':
        for (const widget of this.currentPageWidgets) {
          const element = this.elementFor(widget.id);
          if (element.canWeigh()) {
            availableWidgets.push(widget);
          }
        }

        return availableWidgets;
      case 'addFilter':
        availableWidgets.push(
          ...this.currentPageWidgets.filter((w) => {
            return (
              w.component !== 'DashboardWidgetMarkdownComponent' &&
              w.component !== 'DashboardWidgetNotesComponent' &&
              !w.payload.isSpecial &&
              w.component !== 'DashboardWidgetPredictiveComponent' &&
              w.payload.type !== 'journey'
            );
          })
        );

        return availableWidgets;
    }
  }

  selectAllWidget() {
    this.selectModeStore.setSelected(this.getAvailableWidgets());
  }

  unselectAllWidget() {
    switch (this.selectModeStore.mode()) {
      case 'filters':
        const sourceWidget = this.selectModeStore.selected()[0];
        this.selectModeStore.setSelected([sourceWidget]);
        break;
      case 'widgetGroup':
      case 'widgetGroupBulkEdit': {
        const selection = this.selectModeStore.data().widgetGroup?.isImpact
          ? this.currentPageWidgets.filter(
              (widget) =>
                widget.payload.isSpecial ||
                widget.component === 'DashboardWidgetPredictiveComponent'
            )
          : [];
        this.selectModeStore.setSelected(selection);
        break;
      }
      case 'comparison':
      case 'analyticItem':
      case 'goal':
      case 'dateMarker':
      case 'weight':
      case 'addFilter':
        this.selectModeStore.setSelected([]);
    }
  }

  public async applySelectedWidgets(): Promise<void> {
    const selected = this.selectModeStore.selected();
    const mode = this.selectModeStore.mode();
    const data = this.selectModeStore.data();
    const selectedWidgetIds = selected.map((widget) => widget.id);

    switch (mode) {
      case 'filters':
        for (const widgetId of selectedWidgetIds) {
          this.elementFor(widgetId).applyWidgetFilter(
            data.filters,
            undefined,
            data.searchFilters,
            data.sectionFilter
          );
        }
        break;

      case 'widgetGroup':
        const filterGroup = createWidgetGroup(
          data.name,
          selectedWidgetIds,
          this.currentPageId,
          data.widgetGroupColor
        );

        this.dashboard.widgetGroups[filterGroup.id] = filterGroup;
        const needsRefreshAll = this.widgetGroupStore.getByPage(this.currentPageId).length === 0;
        this.widgetGroupStore.registerWidgetGroup(filterGroup);
        this.widgetGroupRefreshEmit.emit(true);
        this.updateWidgetHasWidgetGroups();

        if (needsRefreshAll) {
          this.refreshWidgets([], true);
        } else {
          this.refreshWidgets(selectedWidgetIds);
        }

        this.currentPageWidgets.forEach((widget) => {
          const widgetElement = this.elementFor(widget.id);
          widgetElement.updateTemplateBooleans();
        });

        this.alertService.showToast(
          translate('dashboard-view.alert.widget-group-create', {
            widgetGroupName: data.name,
          })
        );
        break;

      case 'widgetGroupBulkEdit':
        const groupWidgetIds: string[] = this.currentPageWidgets
          .map((w) => w.id)
          .filter((wId) => data.widgetGroup.widgets.includes(wId));

        const groupParentWidgetId: string = data.widgetGroup.parentWidget;
        const groupId: string = data.widgetGroup.id;

        // Find each item in group widgets that is not in selected widgets (removed items)
        const itemsToRemove = groupWidgetIds.filter((item) => !selectedWidgetIds.includes(item));
        if (itemsToRemove.length !== 0) {
          this.alertService.showToast(
            translate('dashboard-view.alert.widget-removed-from-group', {
              widgetGroupName: data.widgetGroup.title,
            })
          );
        }
        for (const item of itemsToRemove) {
          this.onUpdateWidgetGroupMembership({
            groupId,
            widgetId: item,
            action: 'remove',
          });
        }

        const itemsToAdd = selectedWidgetIds.filter((item) => {
          return !groupWidgetIds.includes(item) && item !== groupParentWidgetId;
        });
        if (itemsToAdd.length !== 0) {
          this.alertService.showToast(
            translate('dashboard-view.alert.widget-added-to-group', {
              widgetGroupName: data.widgetGroup.title,
            })
          );
        }
        for (const item of itemsToAdd) {
          this.onUpdateWidgetGroupMembership({
            groupId,
            widgetId: item,
            action: 'add',
          });
        }

        if (groupParentWidgetId) {
          const parentExists = selectedWidgetIds.includes(groupParentWidgetId);
          if (!parentExists) {
            this.onUpdateWidgetGroupParent({
              groupId,
              widgetId: groupParentWidgetId,
              action: 'remove',
            });
          }
        }
        break;
      case 'comparison':
        const comparisonWidgetIds: string[] = data.comparison.widgets;

        const widgetsToRemove = comparisonWidgetIds.filter(
          (item) => !selectedWidgetIds.includes(item)
        );
        if (widgetsToRemove.length) {
          this.alertService.showToast(
            translate('dashboard-view.alert.widget-removed-from-comparison', {
              comparisonName: data.comparison.title,
            })
          );
        }
        for (const item of widgetsToRemove) {
          this.comparisonService.revokeComparison(
            data.comparison,
            this.currentPageWidgets.find((w) => w.id === item)
          );
        }

        const widgetsToAdd = selectedWidgetIds.filter(
          (item) => !comparisonWidgetIds.includes(item)
        );
        if (widgetsToAdd.length) {
          this.alertService.showToast(
            translate('dashboard-view.alert.widget-added-to-comparison', {
              comparisonName: data.comparison.title,
            })
          );
        }
        for (const item of widgetsToAdd) {
          this.comparisonService.applyComparison(
            data.comparison,
            this.currentPageWidgets.find((w) => w.id === item),
            true
          );
        }
        break;
      case 'analyticItem':
        {
          for (const widget of selected) {
            switch (widget.payload.type) {
              case 'breakdown':
                widget.payload.breakdownOptions.numericMetadataFieldId = data.columnId;
                break;
              case 'donut':
              case 'gauge':
                widget.payload.analyticItem = data.columnId;
                break;
              default:
                continue;
            }
          }

          this.refreshWidgets(selectedWidgetIds);
        }
        break;
      case 'goal':
        {
          selected.forEach((w) => {
            w.payload.chartGoal = data.goal;
            w.payload.chartGoalColor ??= 'rgba(35, 55, 77, 1)';
          });

          this.refreshWidgets(selectedWidgetIds);
        }
        break;
      case 'dateMarker':
        {
          selected.forEach((w) => {
            const payload = w.payload as WidgetChartOptions;
            const dateMarker = data as DateMarker;

            const index = payload.dateMarkers?.findIndex(
              (dm) => dm.displayName === dateMarker.displayName
            );
            if (index >= 0) {
              payload.dateMarkers.splice(index, 1);
            }

            payload.dateMarkers ??= [];
            payload.dateMarkers.push(dateMarker);
          });

          this.refreshWidgets(selectedWidgetIds);
        }
        break;
      case 'weight':
        selected.forEach((w) => {
          const element = this.elementFor(w.id);
          element.setWeightField({ fieldName: data.weightField });
        });
        break;
      case 'addFilter':
        {
          const filterSet = this.filterService.getFilterSetNameByAnalyticItem(data.filter);

          selected.forEach((w) => {
            if (w.payload.filters) {
              const analyticItemFilterSets = w.payload.filters as AnalyticItemFilterSets;
              analyticItemFilterSets[filterSet].and.push(data.filter);
            } else {
              w.payload.filters = FilterService.getDefaultFilterSet();
              w.payload.filters[filterSet].and.push(data.filter);
            }
          });

          this.refreshWidgets(selectedWidgetIds);
        }
        break;
    }
    this.dashboardStore.markDirty();
    this.cancelSelect();
  }

  get applySelectModeButtonText() {
    switch (this.selectModeStore.mode()) {
      case 'filters':
        return translate('dashboard-view.label.apply-filters-button');
      case 'edit':
        return translate('dashboard-view.label.apply-edit-button');
      case 'widgetGroup':
      case 'widgetGroupBulkEdit':
        return translate('dashboard-view.label.apply-widget-group-bulk-button');
      case 'comparison':
        return translate('dashboard-view.label.apply-comparison-button');
      case 'analyticItem':
        return translate('dashboard-view.label.set-analytic-item-button');
      case 'goal':
        return translate('dashboard-view.label.set-goal-button');
      case 'dateMarker':
        return translate('dashboard-view.label.set-date-marker-button');
      case 'weight':
        return translate('dashboard-view.label.set-weight-button');
      case 'addFilter':
        return translate('dashboard-view.label.add-filter-button');
    }
  }

  public onDeleteWidgetGroup(widgetGroup: WidgetGroup) {
    const groupWidgets = JSON.parse(JSON.stringify(widgetGroup.widgets));
    if (widgetGroup.parentWidget) {
      groupWidgets.push(JSON.parse(JSON.stringify(widgetGroup.parentWidget)));
    }
    this.dashboard = deleteWidgetGroup(this.dashboard, widgetGroup);
    this.alertService.showToast(
      translate('dashboard-view.alert.widget-group-delete', {
        widgetGroupName: widgetGroup.title,
      })
    );
    this.widgetGroupStore.unregisterWidgetGroup(widgetGroup);
    this.updateWidgetHasWidgetGroups();

    if (this.dashboard.payload.type === 'predictive') {
      this.currentPageWidgets.forEach((widget) => {
        this.elementFor(widget.id).updateTemplateBooleans();
      });
    }

    if (this.widgetGroupStore.getByPage(this.currentPageId).length > 0) {
      this.refreshWidgets(groupWidgets);
    } else {
      this.refreshWidgets([], true);
    }
    this.dashboardStore.markDirty();
    this.widgetGroupRefreshEmit.emit(true);
  }

  public onUpdateWidgetGroup(
    widgetGroup: WidgetGroup,
    widgetsToRefresh?: string[],
    alertToDisplay?: string,
    updateParent = false,
    showToast = true
  ) {
    let groupWidgets = widgetsToRefresh
      ? [...new Set(widgetsToRefresh)]
      : JSON.parse(JSON.stringify(widgetGroup.widgets));
    if (updateParent && widgetGroup.parentWidget) {
      groupWidgets = groupWidgets.concat(widgetGroup.parentWidget);
    }
    this.dashboard = this.widgetGroupStore.updateWidgetGroup(this.dashboard, widgetGroup);
    if (showToast) {
      this.alertService.showToast(
        alertToDisplay ??
          translate('dashboard-view.alert.widget-group-update', {
            widgetGroupName: widgetGroup.title,
          })
      );
    }
    this.dashboardStore.markDirty();
    this.refreshWidgets(groupWidgets);
    this.widgetGroupRefreshEmit.emit(true);
  }

  public cancelSelect(discardChanges?): void {
    this.widgetGroupRefreshEmit.emit(false);

    if (this.selectModeStore.mode() === 'edit') {
      this.exitEditMode(discardChanges);
    } else {
      this.selectModeStore.reset();
    }
  }

  canReorderPage(): boolean {
    if (!this.dashboard) {
      return false;
    }

    return this.dashboard.locked || this.dashboard.payload.pages.length < 2;
  }

  public setCanAddWidget() {
    this.canAddWidget =
      this.currentPageWidgets.filter(
        (w) => w.payload.type !== 'note' && w.payload.type !== 'noteMarkdown'
      ).length < this.pageManager.maxWidgetsPerPage;
  }

  public onAddWidget(widget?: Widget): void {
    const flatDataSources = Object.values(this.datasourceManagerStore.getDatasources())
      .reduce((prev, curr) => prev.concat(curr), [])
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    let dashboardAnalysisDataSource = this.datasourceManagerStore.getById(
      this.dashboard.payload.lastDataSourceId
    );
    if (!dashboardAnalysisDataSource) {
      dashboardAnalysisDataSource = flatDataSources.filter(
        (ds) => ds instanceof WidgetAnalysisDataSource
      )[0];
    }

    let dashboardKeyDriverAnalysisDataSource = this.datasourceManagerStore.getById(
      this.dashboard.payload.lastKeyDriverDataSourceId
    );
    if (!dashboardKeyDriverAnalysisDataSource) {
      dashboardKeyDriverAnalysisDataSource = flatDataSources.filter(
        (ds) => ds instanceof WidgetKeyDriverAnalysisDataSource
      )[0];
    }

    this.widgetAddDialogData = {
      dashboard: this.dashboard,
      dashboardDataSource: dashboardAnalysisDataSource,
      dashboardKeyDriverDataSource: dashboardKeyDriverAnalysisDataSource,
      dataSources: this.datasourceManagerStore.getDatasources(),
      defaultColorPalette: (this.project.colorPalette ?? DEFAULT_PROJECT_COLOR_PALETTE).map((c) =>
        ChartCustomColorService.colorStringToRgbaColor(c)
      ),
      widget,
    };
    if (widget) {
      this.widgetAddDialogData.widgetGroup = this.getWidgetWidgetGroup(widget.id);
      this.widgetAddDialogData.comparison = this.dashboard.comparisons[widget.payload.comparison];
    }
    this.showWidgetAddDialog = true;
  }

  async onWidgetAddDialogClosed(result?) {
    let widgetDataSource: WidgetDataSource;
    let metadataFields: string[];
    this.showWidgetAddDialog = false;

    if (!result) {
      this.widgetAddDialogData = undefined;
      return;
    }

    if (
      result.payload.type !== 'note' &&
      result.payload.type !== 'noteMarkdown' &&
      result.payload.type !== 'insightFeed'
    ) {
      widgetDataSource = this.datasourceManagerStore.getById(result.dataSourceId);
      metadataFields = await this.customFieldService.getMetadataSubTypesAsync(widgetDataSource);
    }

    if (result.payload.type === 'bigNumber') {
      const bigNumberResult: any = await this.openBigNumberModal(
        result,
        widgetDataSource,
        metadataFields
      );
      if (bigNumberResult === ModalCloseType.CLOSE) {
        return;
      }
      if (bigNumberResult === ModalCloseType.CANCEL) {
        // Reopen 'Add Widget' modal and stop further execution here
        this.onAddWidget();

        return;
      }

      result.payload.analyticItem = bigNumberResult.fieldOne.analyticItem;
      result.payload.customFieldSelected = bigNumberResult.fieldOne.customField;
      result.payload.metadataDisplayNameOne = bigNumberResult.fieldOne.metadataItemDisplayName;
      result.payload.useFieldDisplayNameOne = bigNumberResult.fieldOne.useFieldDisplayName;
      result.payload.scoringStrategy = Constants.TWTRA_DEFAULT_SCORING_STRATEGY;
      result.payload.scoreRange = Constants.TWTRA_DEFAULT_DEPENDENT_RANGE;
      //
      result.payload.analyticItemTwo = bigNumberResult.fieldTwo.analyticItem;
      result.payload.metadataTwoCustomFieldSelected = bigNumberResult.fieldTwo.customField;
      result.payload.metadataDisplayNameTwo = bigNumberResult.fieldTwo.metadataItemDisplayName;
      result.payload.useFieldDisplayNameTwo = bigNumberResult.fieldTwo.useFieldDisplayName;
      result.payload.scoringStrategyTwo = Constants.TWTRA_DEFAULT_SCORING_STRATEGY;
      result.payload.scoreRangeTwo = Constants.TWTRA_DEFAULT_DEPENDENT_RANGE;
    } else if (result.payload.type === 'grid') {
      this.gridAnalyticItemSelectorDialogOptions = {
        widget: result,
        widgetDataSource,
        isNew: false,
        metadataFields,
        sentimentScale: this.dashboard.payload.sentimentScale,
      };

      this.showGridAnalyticItemSelectorDialog = true;

      this.widgetAddDialogData = undefined;
      return;
    } else if (result.payload.type === 'journey') {
      this.journeyAnalyticItemSelectorDialogOptions = {
        widget: result,
        widgetDataSource,
        isNew: false,
        metadataFields,
        sentimentScale: this.dashboard.payload.sentimentScale,
      };

      this.showJourneyAnalyticItemSelectorDialog = true;

      this.widgetAddDialogData = undefined;
      return;
    }

    if (result.payload.type === 'predictor') {
      result.payload.dependentRange = Constants.TWTRA_DEFAULT_DEPENDENT_RANGE;
      result.payload.driversRange = Constants.TWTRA_DEFAULT_DRIVERS_RANGE;
      result.payload.breakdownScoringStrategy = Constants.TWTRA_DEFAULT_SCORING_STRATEGY;
      result.payload.breakdownBoxDirection = Constants.TWTRA_DEFAULT_BOX_DIRECTION;
      result.payload.breakdownBoxSize = Constants.TWTRA_DEFAULT_BOX_SIZE;
      result.payload.driverScoringStrategy = Constants.TWTRA_DEFAULT_SCORING_STRATEGY;
      result.payload.driverBoxDirection = Constants.TWTRA_DEFAULT_BOX_DIRECTION;
      result.payload.driverBoxSize = Constants.TWTRA_DEFAULT_BOX_SIZE;
    }

    if (result.payload.type === 'impact' || result.payload.type === 'focusAreas') {
      this.ratingFieldSelectorData = {
        dataSource: widgetDataSource,
        ratingFieldName: result.payload.ratingField,
        cancelButtonText: translate('widgets.options.back'),
        metadataFields,
        resolveFn: (res) => res,
      };
      const modalClosePromise = new Promise<FieldSelectDialogResult | ModalCloseType>(
        (resolve) => (this.ratingFieldSelectorData.resolveFn = resolve)
      );
      this.ratingTitle =
        result.payload.type === 'focusAreas' ? 'select-field-focus-area' : 'select-field-impact';
      this.showRatingFieldSelectorModal = true;
      const impactResult = await modalClosePromise;
      if (impactResult === ModalCloseType.CLOSE) {
        return;
      }
      if (impactResult === ModalCloseType.CANCEL) {
        this.onAddWidget();
        return;
      }
      result.payload.ratingField = impactResult.field.fieldName;
      result.payload.impactMetric = impactResult.impactMetric;

      // Set ratingScale based on the impact metric type
      if (impactResult.impactMetric === ImpactMetric.BINARY) {
        result.payload.ratingScale = '0:100';
        // Clear bucket properties for BINARY
        result.payload.customBuckets = undefined;
        result.payload.selectedBucketIndex = undefined;
        result.payload.bucketName = undefined;
      } else if (impactResult.impactMetric === ImpactMetric.BUCKETS) {
        const { field, buckets, selectedBucketIndex } = impactResult || {};
        result.ratingFieldName = field.label;
        result.payload.ratingField = field.fieldName;
        result.payload.customBuckets = buckets;
        result.payload.selectedBucketIndex = selectedBucketIndex;
        result.payload.originalBuckets = field.buckets;
        result.payload.bucketName = (buckets || field.buckets)[selectedBucketIndex].bucketName;
        result.payload.fieldBuckets = impactResult.buckets;

        // Save to customBucketsByField for persistence
        if (!result.payload.customBucketsByField) {
          result.payload.customBucketsByField = {};
        }
        result.payload.customBucketsByField[field.fieldName] = {
          buckets: buckets,
          selectedBucketIndex: selectedBucketIndex,
        };

        // Set ratingScale to the bucket's range
        const bucketsToUse = buckets || field.buckets;
        if (bucketsToUse && bucketsToUse.length > 0 && selectedBucketIndex !== undefined) {
          const selectedBucket = bucketsToUse[selectedBucketIndex];
          if (selectedBucket) {
            result.payload.ratingScale = `${selectedBucket.minimumValue}:${selectedBucket.maximumValue}`;
          }
        }
        delete result.payload.scoringScale;
      } else {
        // RATING metric - clear bucket properties and keep default scale
        result.payload.customBuckets = undefined;
        result.payload.selectedBucketIndex = undefined;
        result.payload.bucketName = undefined;
        result.payload.scoringStrategy = Constants.TWTRA_DEFAULT_SCORING_STRATEGY;
        // ratingScale already set to Constants.TWTRA_DEFAULT_DRIVERS_RANGE in widget-add
      }

      if (result.payload.type === 'focusAreas' && !widgetDataSource.hasTaxonomy) {
        const getReport = (payload = result.payload): AbstractChartReport => {
          const reportOptions = JSON.parse(JSON.stringify(payload));
          reportOptions['widgetDataSource'] = widgetDataSource;
          return this.chartReportService.configureReport(reportOptions);
        };

        const analyticItemTypes = await AnalyticItemTypes.generateMapFromDataSource(
          widgetDataSource,
          this.accountService,
          this.customFieldService
        );

        this.analyticItemModalOptions = {
          widget: result,
          widgetDataSource,
          analyticItemTypes,
          report: getReport(),
          resolveFn: (res) => res,
        };

        const analyticPromise = new Promise<any | null>(
          (resolve) => (this.analyticItemModalOptions.resolveFn = resolve)
        );
        this.showAnalyticItemModal = true;
        const analyticItem = await analyticPromise;
        if (!analyticItem) {
          this.onAddWidget();
          return;
        }
        result.payload['analyticItemResult'] = analyticItem;
        this.showAnalyticItemModal = false;
      }
    }
    this.datasourceManagerStore.changeDataSource(result.id, widgetDataSource?.id);

    await this.redrawAfterWidgetAddDialogClose(result);

    this.widgetAddDialogData = undefined;
  }

  async onCloseGridAnalyticItemSelectorDialog(
    result: GridAnalyticItemSelectorDialogResult | null | ModalCloseType
  ): Promise<void> {
    if (result && result !== ModalCloseType.CANCEL && result !== ModalCloseType.CLOSE) {
      setGridAnalyticItemSelectorDialogResultOnPayload(
        this.gridAnalyticItemSelectorDialogOptions.widget.payload as WidgetGridOptions,
        result
      );

      this.datasourceManagerStore.changeDataSource(
        this.gridAnalyticItemSelectorDialogOptions.widget.id,
        this.gridAnalyticItemSelectorDialogOptions.widgetDataSource?.id
      );
      await this.redrawAfterWidgetAddDialogClose(this.gridAnalyticItemSelectorDialogOptions.widget);
    }

    if (result === ModalCloseType.CANCEL) {
      this.onAddWidget();
    }

    this.showGridAnalyticItemSelectorDialog = false;
  }

  async onCloseJourneyAnalyticItemSelectorDialog(
    result: JourneyAnalyticItemSelectorDialogResult | null | ModalCloseType
  ): Promise<void> {
    if (result && result !== ModalCloseType.CANCEL && result !== ModalCloseType.CLOSE) {
      this.journeyAnalyticItemSelectorDialogOptions.widget.payload.numericMetadataFields =
        result.numericMetadataFields;
      this.datasourceManagerStore.changeDataSource(
        this.journeyAnalyticItemSelectorDialogOptions.widget.id,
        this.journeyAnalyticItemSelectorDialogOptions.widgetDataSource?.id
      );
      await this.redrawAfterWidgetAddDialogClose(
        this.journeyAnalyticItemSelectorDialogOptions.widget
      );
    }

    if (result === ModalCloseType.CANCEL) {
      this.onAddWidget();
    }

    this.showJourneyAnalyticItemSelectorDialog = false;
  }

  private async redrawAfterWidgetAddDialogClose(result?): Promise<void> {
    result.payload.page = this.currentPageId;
    this.widgetLoadingManager.onBeginLoading(result.id);
    this.changedWidgets.add(result.id);
    this.forceRedraw(
      () => {
        if (this.widgetAddDialogData?.widget) {
          const idx = this.dashboard.widgets.findIndex(
            (w) => w.id === this.widgetAddDialogData.widget.id
          );
          if (idx !== -1) {
            // protect the refresh property from being overwritten
            const r = this.widgetAddDialogData.widget.payload.refresh;
            this.dashboard.widgets[idx] = result;
            this.dashboard.widgets[idx].payload.refresh = r;
            this.currentPageWidgets = this.dashboard.widgets.filter(
              (w) => w.payload.page === this.currentPageId
            );
            this.dashboardStore.markDirty();
            return;
          }
        }
        const positionItem = this.dashboardOptions.api.getFirstPossiblePosition(result);
        result.x = positionItem.x;
        result.y = positionItem.y;

        if (result.dataSourceId) {
          if (result.payload.type === 'predictor') {
            this.dashboard.payload.lastKeyDriverDataSourceId = result.dataSourceId;
          } else {
            this.dashboard.payload.lastDataSourceId = result.dataSourceId;
          }
        }
        this.dashboard.widgets.push(result);
        this.dashboardStore.markDirty();
        this.currentPageWidgets = [...this.currentPageWidgets, result];
      },
      async () => {
        if (this.widgetAddDialogData?.widget) {
          const idx = this.currentPageWidgets.findIndex(
            (w) => w.id === this.widgetAddDialogData.widget.id
          );
          if (idx !== -1) {
            const widgetComponent = this.componentFor(this.widgetAddDialogData.widget.id);
            await widgetComponent.goBackToRoot();
          }
        } else {
          const dataSource = this.datasourceManagerStore.getById(result.dataSourceId);
          if (result.payload.type === 'focusAreas' && !dataSource.hasTaxonomy) {
            this.elementFor(result.id).setAnalyticType(result.payload['analyticItemResult']);
          }
          this.elementFor(result.id).scrollIntoView();
        }
        this.setCanAddWidget();
        //update saved order so user can trigger arrangement
        this.requestReflowWithAppended(result.id);
      }
    );
  }

  private async openBigNumberModal(
    widget: Widget,
    widgetDataSource: WidgetDataSource,
    metadataFields: string[]
  ) {
    return new Promise((resolve: any) => {
      this.dialogService
        .showDialog<BigNumberMetadataSelectorDialogComponent, BigNumberMetadataDialogOptions>(
          BigNumberMetadataSelectorDialogComponent,
          {
            widget,
            widgetDataSource,
            newWidget: true,
            metadataFields,
          },
          {},
          false
        )
        .afterClosed()
        .subscribe((result) => {
          resolve(result);
        });
    });
  }

  private createCloneWidget(widget: Widget, page: number): Widget {
    const clone: Widget = Utils.deepCloneWithNulls(widget);
    clone.id = UUIDv4();
    clone.payload.page = page;
    clone.payload.comments = clone.payload.comments?.filter((c) => !c.smartSummaryId);
    clone.payload.comments?.forEach((c) => (c.payload.widgetId = clone.id));

    return clone;
  }

  public onCloneWidget(widget: Widget, pageId?: number): void {
    const clone = this.createCloneWidget(widget, pageId || this.currentPageId);

    if (pageId) {
      delete clone.payload.comparison;

      const group = this.widgetGroupStore.getByWidget(widget.id);
      if (isDashboardLevelWidgetGroup(group)) {
        this.onUpdateWidgetGroupMembership({
          groupId: group.id,
          widgetId: clone.id,
          action: 'add',
        });
      }

      this.dashboard.widgets.push(clone);

      this.alertService.showToast(
        translate('dashboard-view.alert.widget-clone', {
          pageName: this.dashboard.payload.pages.find((p) => p.id === pageId).title,
        })
      );
      this.dashboardStore.markDirty();

      this.onCurrentPageChanged(pageId);
    } else {
      this.forceRedraw(
        () => {
          this.dashboard.widgets.push(clone);

          const positionItem = this.dashboardOptions.api.getFirstPossiblePosition(clone);
          clone.x = positionItem.x;
          clone.y = positionItem.y;

          this.currentPageWidgets = [...this.currentPageWidgets, clone];
          this.dashboardStore.markDirty();
        },
        () => {
          this.setCanAddWidget();
          //update saved order so user can trigger arrangement
          this.requestReflowWithAppended(clone.id);
        }
      );

      this.initializeWidgetClone(widget, clone);
    }
  }

  private initializeWidgetClone(widget: Widget, clone: Widget) {
    this.widgetLoadingManager.onBeginLoading(clone.id);

    const group = this.widgetGroupStore.getByWidget(widget.id);
    if (group) {
      this.onUpdateWidgetGroupMembership({
        groupId: group.id,
        widgetId: clone.id,
        action: 'add',
      });
    }

    const comparisonId = widget.payload.comparison;
    if (comparisonId) {
      this.comparisonService.applyComparison(this.dashboard.comparisons[comparisonId], clone, true);
    }

    this.elementFor(clone.id).initWidgetActions();
    this.componentFor(clone.id).filteredDocumentCount = this.componentFor(
      widget.id
    ).filteredDocumentCount;
    this.elementFor(clone.id).scrollIntoView();
    this.datasourceManagerStore.changeDataSource(clone.id, widget.dataSourceId);

    this.alertService.showToast(translate('dashboard-view.alert.widget-clone-initialize'));
  }

  onMoveWidget(widget: Widget, pageId) {
    // for gridster a new reference needed to place the widget to the end
    this.dashboard.widgets.splice(this.dashboard.widgets.indexOf(widget), 1);

    const clone = Utils.deepCloneWithNulls(widget);
    clone.payload.page = pageId;
    clone.x = 0;
    clone.y = 0;

    this.dashboard.widgets.push(clone);

    const group = this.widgetGroupStore.getByWidget(widget.id);
    if (group && !isDashboardLevelWidgetGroup(group)) {
      this.onUpdateWidgetGroupMembership({
        groupId: group.id,
        widgetId: widget.id,
        action: 'remove',
        silent: true,
      });
    }

    const comparisonId = clone.payload.comparison;
    if (comparisonId) {
      this.comparisonService.revokeComparison(this.dashboard.comparisons[comparisonId], clone);
    }

    this.alertService.showToast(
      translate('dashboard-view.alert.widget-move', {
        pageName: this.dashboard.payload.pages.find((p) => p.id === pageId).title,
      })
    );
    this.dashboardStore.markDirty();

    this.onCurrentPageChanged(pageId);
  }

  public onDeleteWidget(widget: Widget): void {
    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-view.label.delete-widget-dialog'),
        content: translate('dashboard-view.text.delete-widget-dialog'),
        confirmTitle: translate('dashboard-view.label.confirm-delete-widget-dialog'),
        cancelTitle: translate('dashboard-view.label.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.datasourceManagerStore.removeDataSourceFromWidgetMap(widget.id, widget.dataSourceId);
          const group = this.widgetGroupStore.getByWidget(widget.id);
          if (group) {
            this.onUpdateWidgetGroupMembership({
              groupId: group.id,
              widgetId: widget.id,
              action: 'remove',
            });
          }

          if (widget.payload.comparison) {
            this.comparisonService.revokeComparison(
              this.dashboard.comparisons[widget.payload.comparison],
              widget
            );
          }

          for (let i = 0; i < this.dashboard.widgets.length; ++i) {
            const item = this.dashboard.widgets[i];
            if (item.id === widget.id) {
              this.forceRedraw(
                () => {
                  this.dashboard.widgets.splice(i, 1);
                  this.currentPageWidgets = this.currentPageWidgets.filter(
                    (cpw) => cpw.id !== widget.id
                  );
                  this.dashboardStore.markDirty();
                  this.setCanAddWidget();
                },
                () => {
                  // update saved order so user can trigger arrangement
                  this.requestReflowWithRemoved(widget.id);
                }
              );
              break;
            }
          }
        }
      });
  }

  private update_last_accessed() {
    if (this.isDashboardNew) {
      return;
    }

    try {
      this.dashboardService.markAccessed(this.dashboard);
    } catch (error) {
      //Nothing to do just continue on.
      //TODO: do we want to alert the user at all here?
    }
  }

  public get canEditDashboardName(): boolean {
    return !this.isReadOnly && this.isAdmin;
  }

  public async beforeLogout(): Promise<boolean> {
    if (this.isUnsaved) {
      return new Promise<boolean>((resolve) => {
        this.dialogService
          .showSimpleDialog({
            title: translate('dashboard-view.label.unsaved-changes-dialog'),
            content: translate('dashboard-view.text.unsaved-changes-dialog'),
            confirmTitle: translate('dashboard-view.label.confirm-unsaved-changes-dialog'),
            cancelTitle: translate('dashboard-view.label.cancel'),
            confirmColor: SimpleDialogButtonColor.blue,
            buttons: [
              {
                title: translate('dashboard-view.label.discard-unsaved-changes-dialog'),
                color: SimpleDialogButtonColor.orange,
                handler: (dialogRef) => {
                  dialogRef.close();
                  this.changeDetectorRef.detach();
                  this.dashboardService.removeCachedDashboard(
                    this.dashboardId,
                    this.projectId,
                    this.session.info2.user_id
                  );
                  resolve(true);
                },
              },
            ],
          })
          .afterClosed()
          .subscribe(async (result) => {
            if (result) {
              this.changeDetectorRef.detach();
              await this.saveDashboard();
              resolve(true);
            }
          });
      });
    } else {
      return true;
    }
  }

  public async clearUserCache(showToast = true) {
    try {
      await this.collectionService.clearCollectionsInProjectCache(this.projectId);
      if (showToast) {
        this.alertService.showToast(translate('dashboard-view.alert.clear-user-cache'));
      }

      this.reload();
    } catch (error) {
      this.alertService.showErrorToast(
        translate('dashboard-view.alert.clear-user-cache-fail'),
        error
      );
      return;
    }
  }

  onWidgetLoading(initialLoad: boolean, widgetId: string) {
    this.widgetLoadingManager.onBeginLoading(widgetId);
  }

  onWidgetLoaded(initialLoad: boolean, widgetId: string) {
    if (initialLoad) {
      const widgetComponent = this.elementFor(widgetId);
      if (widgetComponent) {
        const component = widgetComponent.getComponent();
        widgetComponent.load();
        component.setWidgetGroup(this.widgetGroupStore.getByWidget(widgetId));
        const widgetType = `${widgetComponent.widget.component}-${
          widgetComponent.widget.payload.type ? widgetComponent.widget.payload.type : 'unk'
        }`;
        this.reportTiming('widget_loaded', { widgetType });
      }
    }

    // delay this so that the highcharts animation has time to complete
    setTimeout(() => {
      this.widgetLoadingManager.onDoneLoading(widgetId);
    }, 1500);
  }

  enterEditMode() {
    this.dashboardOptions.resizable.enabled = true;
    this.dashboardOptions.draggable.enabled = true;
    this.dashboardOptions.displayGrid = DisplayGrid.Always;

    this.currentPageWidgets.forEach((widget) => {
      const component = this.componentFor(widget.id);

      if (component?.minWidth !== this.dashboardOptions.minItemCols) {
        widget.minItemCols = component.minWidth;
      }

      if (component?.minHeight !== this.dashboardOptions.minItemRows) {
        widget.minItemRows = component.minHeight;
      }

      if (
        widget.payload.isSpecial ||
        (widget.component === 'DashboardWidgetPredictiveComponent' &&
          widget.payload.type !== 'predictor')
      ) {
        widget.dragEnabled = false;
        widget.resizeEnabled = false;
        widget.compactEnabled = false;
      }

      if (widget.payload.type === 'predictor') {
        widget.resizeEnabled = false;
      }
    });

    this.dashboardOptions.api.optionsChanged();

    this.editModeHistory = [this.createHistoryEntry()];
    this.editModeHistoryPointer = 0;

    this.selectModeStore.setSelectMode('edit');
  }

  togglePageDetailsVisibility() {
    this.arePageWidgetDetailsHidden = !this.arePageWidgetDetailsHidden;
    this.currentPage.widgetDetailsHidden = this.arePageWidgetDetailsHidden;
    const translationKey = this.arePageWidgetDetailsHidden
      ? 'dashboard-view.alert.toggle-page-details-hidden'
      : 'dashboard-view.alert.toggle-page-details-displayed';
    this.alertService.showToast(translate(translationKey, { pageName: this.currentPage.title }));
  }

  onDropWidget(widget: GridsterItem) {
    this.addHistoryStep(widget, 'move');
    this.dashboardStore.markDirty();
  }

  onResizeWidget(widget: GridsterItem) {
    if (this.shouldIgnoreWidgetChange || !this.dashboardOptions.resizable.enabled) {
      return;
    }
    this.addHistoryStep(widget, 'resize');
    this.dashboardStore.markDirty();

    this.redraw([widget], true);
  }

  private addHistoryStep(affectedWidget: GridsterItem, action: 'move' | 'resize') {
    this.editModeHistory.splice(
      ++this.editModeHistoryPointer,
      Infinity,
      this.createHistoryEntry(affectedWidget, action)
    );

    this.logger.debug('History is at step ', {
      editModeHistoryPointer: this.editModeHistoryPointer,
      editModeHistory: this.editModeHistory,
    });
  }

  private createHistoryEntry(
    affectedWidget?: GridsterItem,
    action?: 'move' | 'resize'
  ): GridsterItem[] {
    return this.currentPageWidgets.map((widget) => ({
      x: widget.x,
      y: widget.y,
      cols: widget.cols,
      rows: widget.rows,
      id: widget.id,
      action: affectedWidget?.id === widget.id && action,
    }));
  }

  undoEditStep() {
    this.applyHistoryStep(--this.editModeHistoryPointer);

    this.redrawWidgetAfterUndoRedo(this.editModeHistoryPointer + 1);

    this.dashboardOptions.api.optionsChanged();
  }

  redoEditStep() {
    this.applyHistoryStep(++this.editModeHistoryPointer);

    this.redrawWidgetAfterUndoRedo(this.editModeHistoryPointer);

    this.dashboardOptions.api.optionsChanged();
  }

  private redrawWidgetAfterUndoRedo(stateIndex) {
    const affectedWidget = this.editModeHistory[stateIndex].find(
      (historyEntry) => historyEntry.action === 'resize'
    );

    if (affectedWidget) {
      setTimeout(() => this.redraw([affectedWidget], true), 300);
    }
  }

  private applyHistoryStep(index) {
    this.editModeHistory[index].forEach((historyEntry) => {
      const widget = this.currentPageWidgets.find((w) => w.id === historyEntry.id);

      widget.x = historyEntry.x;
      widget.y = historyEntry.y;
      widget.cols = historyEntry.cols;
      widget.rows = historyEntry.rows;
    });
  }

  exitEditMode(discardChanges) {
    if (discardChanges) {
      for (let i = this.editModeHistory.length - 1; i >= 0; i--) {
        this.applyHistoryStep(i);
      }

      if (this.editModeHistoryPointer) {
        setTimeout(() => this.redraw(this.currentPageWidgets, true), 300);
      }
    }

    this.dashboardOptions.resizable.enabled = false;
    this.dashboardOptions.draggable.enabled = false;
    this.dashboardOptions.displayGrid = DisplayGrid.None;

    this.currentPageWidgets.forEach((widget) => {
      delete widget.minItemCols;
      delete widget.minItemRows;
      delete widget.dragEnabled;
      delete widget.resizeEnabled;
      delete widget.compactEnabled;
      this.elementFor(widget.id).originalWidget = Utils.deepCloneWithNulls(widget);
    });

    this.dashboardOptions.api.optionsChanged();

    this.selectModeStore.reset();
  }

  // Manual layout action from Edit Page Layout toolbar
  onAutoArrangeClick(): void {
    const orderIds = this.getCurrentPageWidgetIdsByVisualOrder();
    this.reflowRowMajor(orderIds);
  }

  onCustomSortModeChanged(
    isOpen: boolean,
    widgetId: string,
    gridsterItem: GridsterItemComponentInterface
  ) {
    if (isOpen) {
      gridsterItem.el.scrollIntoView();
    }

    this.customSortMode.enabled = isOpen;
    this.customSortMode.widget = widgetId;

    const transformMatrixToNumbers = (element) => {
      const originalTransform = getComputedStyle(element).transform;
      const transformMatch = originalTransform.match(/^matrix\((.+)\)$/);
      const matrixValues = transformMatch[1].split(',').map(parseFloat);

      return { left: matrixValues[4], top: matrixValues[5] };
    };

    if (isOpen) {
      const position = transformMatrixToNumbers(gridsterItem.el);

      this.renderer.removeStyle(gridsterItem.el, 'transform');
      this.renderer.setStyle(gridsterItem.el, 'left', `${position.left + this.GRID_MARGIN}px`);
      this.renderer.setStyle(gridsterItem.el, 'top', `${position.top + this.GRID_MARGIN}px`);
    } else {
      const offsetX = parseInt(gridsterItem.el.style.left, 10) - this.GRID_MARGIN;
      const offsetY = parseInt(gridsterItem.el.style.top, 10) - this.GRID_MARGIN;

      this.renderer.setStyle(
        gridsterItem.el,
        'transform',
        `translate3d(${offsetX}px, ${offsetY}px, 0)`
      );
      this.renderer.removeStyle(gridsterItem.el, 'left');
      this.renderer.removeStyle(gridsterItem.el, 'top');
    }
  }

  onSyncItemColors(colorObject: CustomItemColor) {
    this.chartCustomColorService.addColorToDictionary(colorObject);
    for (const widget of this.dashboard.widgets) {
      const component = this.componentFor(widget.id);
      const { widgetPayloadCustomColors, isComponentDirty } =
        this.chartCustomColorService.syncWidgetColor(widget, colorObject);
      if (widgetPayloadCustomColors) {
        widget.payload.customItemColors = widgetPayloadCustomColors;
      }
      if (component) {
        component.isDirty = isComponentDirty;
      }
    }

    this.dashboardStore.markDirty();
    this.refreshDirtyWidgets();
  }

  onResetItemColor(pointId: string) {
    this.chartCustomColorService.removeColorFromDictionaryByPointId(pointId);
    for (const widget of this.dashboard.widgets) {
      const removed = this.chartCustomColorService.removeWidgetColorForPoint(widget, pointId);
      if (removed) {
        const component = this.componentFor(widget.id);
        if (component) {
          component.isDirty = true;
        }
      }
    }

    this.dashboardStore.markDirty();
    this.refreshDirtyWidgets();
  }

  onUpdateWidgetGroupMembership(result) {
    const groupId = result.groupId;
    const widgetId = result.widgetId;
    const action = result.action;
    const skipRefresh = result.skipRefresh;
    const silent = result.silent;
    const group: WidgetGroup = this.widgetGroupStore.getById(groupId);

    const removeAction = (widgetGroup, wId) => {
      let changedWidgets = [widgetId];

      widgetGroup.widgets.forEach((item, index) => {
        if (item === wId) {
          widgetGroup.widgets.splice(index, 1);
        }
      });
      if (widgetGroup.parentWidget && widgetGroup.parentWidget === widgetId) {
        widgetGroup.parentFilters = FilterService.getDefaultFilterSet();
        delete widgetGroup.parentWidget;
        changedWidgets = JSON.parse(JSON.stringify(widgetGroup.widgets));
      }

      return changedWidgets;
    };

    let widgetsToRefresh = [widgetId];

    if (action === 'remove') {
      widgetsToRefresh = removeAction(group, widgetId);
      if (skipRefresh) {
        widgetsToRefresh = [];
      }
    } else {
      const existingGroup = this.widgetGroupStore.getByWidget(widgetId);
      if (existingGroup) {
        removeAction(existingGroup, widgetId);
      }
      group.widgets.push(widgetId);
    }

    let displayString = '';
    if (action === 'remove') {
      displayString = translate('dashboard-view.alert.widget-removed-from-group', {
        widgetGroupName: group.title,
      });
    } else {
      displayString = translate('dashboard-view.alert.widget-added-to-group', {
        widgetGroupName: group.title,
      });
    }
    this.onUpdateWidgetGroup(group, widgetsToRefresh, displayString, false, !silent);
  }

  onUpdateWidgetGroupParent(result) {
    const groupId = result['groupId'];
    const widgetId = result['widgetId'];
    const action = result['action'];
    const group = this.widgetGroupStore.getById(groupId);
    const previousParent = group?.parentWidget;
    group.parentFilters = FilterService.getDefaultFilterSet();
    if (action === 'remove') {
      if (previousParent === widgetId) {
        delete group.parentWidget;
      }
      group.widgets.forEach((item, index) => {
        if (item === widgetId) {
          group.widgets.splice(index, 1);
        }
      });
    } else if (action === 'demote') {
      delete group.parentWidget;
      if (previousParent) {
        group.widgets.push(previousParent);
      }
    } else {
      group.widgets.forEach((item, index) => {
        if (item === widgetId) {
          group.widgets.splice(index, 1);
        }
      });
      delete group.parentWidget;
      if (previousParent) {
        group.widgets.push(previousParent);
      }
      group.parentWidget = widgetId;
    }
    let displayString = '';
    if (action === 'remove') {
      displayString = translate('dashboard-view.alert.widget-removed-from-group', {
        widgetGroupName: group.title,
      });
    } else if (action === 'demote') {
      displayString = translate('dashboard-view.alert.widget-demoted-from-parent', {
        widgetGroupName: group.title,
      });
    } else {
      displayString = translate('dashboard-view.alert.widget-promoted-to-parent', {
        widgetGroupName: group.title,
      });
    }
    let widgetsToUpdate = [widgetId];
    if (previousParent) {
      widgetsToUpdate.push(previousParent);
    }
    widgetsToUpdate = widgetsToUpdate.concat(JSON.parse(JSON.stringify(group.widgets)));
    this.onUpdateWidgetGroup(group, widgetsToUpdate, displayString, true);
  }

  onAddWidgetGroupDateFilter(result) {
    const groupId = result['groupId'];
    const filterValue = result['filter'];
    const group = this.widgetGroupStore.getById(groupId);
    if (group) {
      const existingFilter = group.filters.dates.and.filter((filt) => filt.value === filterValue);
      if (existingFilter.length > 0) {
        this.alertService.showToast(
          translate('dashboard-view.alert.widget-group-filter-date-exists', {
            groupName: group.title,
          })
        );
      } else {
        group.filters.dates.and.push({
          analyticItem: 'createdAt',
          value: filterValue,
          key: filterValue,
        });
        const toRefresh = JSON.parse(JSON.stringify(group.widgets));
        if (group.parentWidget) {
          toRefresh.push(group.parentWidget);
        }
        this.onUpdateWidgetGroup(
          group,
          toRefresh,
          translate('dashboard-view.alert.widget-group-filter-added', {
            groupName: group.title,
          })
        );
      }
    }
  }

  private get currentPage() {
    return this.getPageById(this.currentPageId);
  }

  @DisableMethodTelemetry() // FIXME: this gets called every tick
  private getPageById(pageId, source = this.dashboard.payload.pages) {
    return source.find((p) => p.id === pageId);
  }

  isPageUnsaved(pageId: string): boolean {
    if (!this.dashboard) {
      return false;
    }
    const id = parseInt(pageId, 10);
    const page = this.getPageById(id);
    return this.pageChangeData.updated.has(page) || this.pageChangeData.added.has(page);
  }

  onCurrentPageChanged(pageId, beforeChangeFn?, afterChangeFn?) {
    pageId = parseInt(pageId, 10);
    if (this.selectModeStore.mode()) {
      return;
    }

    if (this.currentPageId === pageId) {
      beforeChangeFn && beforeChangeFn();

      afterChangeFn && afterChangeFn();

      return;
    }

    this.currentPageId = pageId;
    this.currentPageTabId = pageId.toString();
    this.currentPageWidgets = this.dashboard.widgets.filter(
      (w) => w.payload.page === this.currentPageId
    );
    // Initialize saved order for this page if absent
    if (!this.getSavedOrderForCurrentPage()) {
      const initialOrder = [...this.currentPageWidgets]
        .sort((a, b) => a.y - b.y || a.x - b.x)
        .map((w) => w.id);
      this.saveOrderForCurrentPage(initialOrder);
    }
    this.currentPageWidgetsShowing = false;
    this.isCurrentPageSpecial =
      this.dashboard.payload.type === 'predictive' &&
      this.currentPageWidgets.some((w) => w.component === 'DashboardWidgetPredictiveComponent');

    this.arePageWidgetDetailsHidden = this.currentPage.widgetDetailsHidden;
    this.setCanAddWidget();

    this.dashboardPageChangedEmit.emit(this.currentPageId);
    this.comparisonService.loadComparisons(this.dashboard.comparisons, this.currentPageId);
    this.actionPlanSidePaneService.load(this.dashboard.payload.actionPlans, this.currentPageId);
    this.datasourceManagerStore.clearDataSourceWidgetMap();

    this.forceRedraw(beforeChangeFn, afterChangeFn);
  }

  isPageFull(page) {
    return this.pageManager.isWidgetCountOverTheLimit(this.dashboard, page.id);
  }

  onPageRenameOpened() {
    this.showPageRenameModal = true;

    this.pageRenameOptions = {
      currentPage: this.currentPage,
      pages: this.dashboard.payload.pages,
    };
  }

  onPageRenameClosed($event) {
    this.showPageRenameModal = false;

    if ($event) {
      this.currentPage.title = $event;
      this.dashboardPages.find((p) => p.id === `${this.currentPageId}`).title = $event;
      this.dashboardStore.markDirty();
    }
    this.pageChangeData.updated.add(this.getPageById(this.currentPageId));
  }

  clonePage() {
    const currentPageIdx = this.dashboard.payload.pages.findIndex(
      (p) => p.id === this.currentPageId
    );
    const newPage = this.createNewPage(currentPageIdx + 1);

    const widgetIdMap = new Map<string, string>();

    this.currentPageWidgets.forEach((widget) => {
      const clone = Utils.deepCloneWithNulls(widget);
      widgetIdMap.set(clone.id, UUIDv4());
      clone.id = widgetIdMap.get(clone.id);
      clone.payload.page = newPage.id;
      clone.payload.comments = clone.payload.comments?.filter((c) => !c.smartSummaryId);

      this.dashboard.widgets.push(clone);

      const group = this.widgetGroupStore.getByWidget(widget.id);
      if (isDashboardLevelWidgetGroup(group)) {
        this.onUpdateWidgetGroupMembership({
          groupId: group.id,
          widgetId: clone.id,
          action: 'add',
          silent: true,
        });
      }
    });

    this.widgetGroupStore
      .getByPage(this.currentPageId)
      .filter((group) => !isDashboardLevelWidgetGroup(group))
      .forEach((group) => {
        const clone = this.widgetGroupStore.cloneWidgetGroup(group);

        clone.page = newPage.id;
        clone.widgets = group.widgets.map((id) => widgetIdMap.get(id));
        clone.parentWidget = widgetIdMap.get(group.parentWidget);

        this.dashboard.widgetGroups[clone.id] = clone;
        this.widgetGroupStore.registerWidgetGroup(clone);
        this.widgetGroupRefreshEmit.emit(true);
        this.updateWidgetHasWidgetGroups();
      });

    Object.values(this.dashboard.comparisons)
      .filter((comparison) => comparison.page === this.currentPageId)
      .forEach((comparison) => {
        const clone = Utils.deepCloneWithNulls(comparison);

        clone.id = UUIDv4();
        clone.page = newPage.id;
        clone.widgets = comparison.widgets.map((id) => widgetIdMap.get(id)).filter((id) => !!id);

        for (const widgetId of clone.widgets) {
          const newWidget = this.dashboard.widgets.find((w) => w.id === widgetId);
          if (newWidget) {
            newWidget.payload.comparison = clone.id;
          }
        }

        this.dashboard.comparisons[clone.id] = clone;
      });

    this.dashboard.payload.actionPlans
      .filter((a) => a.page === this.currentPageId)
      .forEach((a) => {
        this.dashboard.payload.actionPlans.push({ id: a.id, page: newPage.id });
      });

    const currentPageTitle = this.currentPage.title;

    this.onCurrentPageChanged(newPage.id);
    this.dashboardStore.markDirty();

    this.alertService.showToast(
      translate('dashboard-view.alert.page-cloned-from-current', {
        newPageName: newPage.title,
        currentPageName: currentPageTitle,
      })
    );
  }

  get deletePageTooltip() {
    if (this.dashboard?.payload.pages.length === 1) {
      return translate('dashboard-view.tooltip.cannot-delete-last-page');
    }
  }

  onPageDeleteOpened() {
    if (this.deletePageTooltip) {
      return;
    }

    this.dialogService
      .showWarningPopup({
        title: translate('dashboard-view.label.delete-page'),
        content: translate('dashboard-view.text.delete-page-content'),
        confirmTitle: translate('dashboard-view.label.delete-page'),
        cancelTitle: translate('dashboard-view.label.cancel'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe(async (result) => {
        if (result) {
          const pageName = this.currentPage.title;
          const pageWidgetGroups = this.widgetGroupStore
            .getByPage(this.currentPageId)
            .filter((group) => !isDashboardLevelWidgetGroup(group));

          const dashboardLevelWidgetGroup = getDashboardLevelWidgetGroup(this.dashboard);
          let dashboardLevelWidgetGroupMembers: string[] = [];
          if (dashboardLevelWidgetGroup) {
            dashboardLevelWidgetGroupMembers = this.currentPageWidgets
              .filter((w) => this.widgetGroupStore.hasDashboardLevelWidgetGroupApplied(w.id))
              .map((w) => w.id);
          }

          pageWidgetGroups.forEach((wg) => {
            deleteWidgetGroup(this.dashboard, wg);
            this.widgetGroupStore.unregisterWidgetGroup(wg);
          });

          if (dashboardLevelWidgetGroup) {
            dashboardLevelWidgetGroup.widgets = dashboardLevelWidgetGroup.widgets.filter(
              (id) => !dashboardLevelWidgetGroupMembers.includes(id)
            );
            this.widgetGroupStore.updateWidgetGroup(this.dashboard, dashboardLevelWidgetGroup);
          }

          this.dashboard.widgets = this.dashboard.widgets.filter(
            (w) => w.payload.page !== this.currentPageId
          );

          const comparisonsToDelete = Object.values(this.dashboard.comparisons)
            .filter((c) => c.page === this.currentPageId)
            .map((c) => c.id);

          comparisonsToDelete.forEach((c) => delete this.dashboard.comparisons[c]);

          this.dashboard.payload.actionPlans = this.dashboard.payload.actionPlans.filter(
            (a) => a.page !== this.currentPageId
          );

          const currentPageIdx = this.dashboardPages.findIndex(
            (p) => p.id === `${this.currentPageId}`
          );
          this.dashboard.payload.pages.splice(currentPageIdx, 1);
          this.dashboardPages.splice(currentPageIdx, 1);

          this.currentPageId = undefined;
          this.currentPageTabId = undefined;
          const pageIdxToSwitch = Math.min(currentPageIdx, this.dashboardPages.length - 1);
          this.updateCanAddPage();
          this.onCurrentPageChanged(this.dashboardPages[pageIdxToSwitch].id);

          this.alertService.showToast(
            translate('dashboard-view.alert.named-page-deleted', {
              deletedPageName: pageName,
            })
          );
          this.dashboardStore.markDirty();
        }
      });
  }

  addPage() {
    const newPage = this.createNewPage();

    this.onCurrentPageChanged(newPage.id);

    this.alertService.showToast(
      translate('dashboard-view.alert.new-page-created', {
        newPageName: newPage.title,
      })
    );
    this.dashboardStore.markDirty();
  }

  private createNewPage(newPageIdx = this.dashboard.payload.pages.length) {
    const newPageId =
      this.dashboard.payload.pages.reduce((prev, curr) => Math.max(prev, curr.id), 0) + 1;

    const newPage = {
      id: newPageId,
      title: translate('dashboard-view.label.page-number', {
        pageId: newPageId,
      }),
    };

    this.dashboard.payload.pages.splice(newPageIdx, 0, newPage);
    this.dashboardPages.splice(newPageIdx, 0, { title: newPage.title, id: `${newPage.id}` });
    this.updateCanAddPage();

    return newPage;
  }

  onPageReorderClosed($event) {
    this.showPageReorderModal = false;

    if ($event) {
      this.dashboard.payload.pages = $event;
      this.dashboardStore.markDirty();
      this.recalculateTabs();
    }
  }

  private recalculateTabs() {
    this.dashboardPages = this.dashboard.payload.pages.map((p) => ({
      title: p.title,
      id: `${p.id}`,
      disabled: !!this.selectModeStore.mode(),
      disabledReason: translate('dashboard-view.tooltip.action-disabled-bulk-apply'),
    }));
  }

  public canShareDashboard(): boolean {
    if (this.isAdmin) {
      return true;
    }

    return !this.authService.isReadOnly;
  }

  onOpenApplyComparison($event) {
    this.showApplyComparisonModal = true;

    this.applyComparisonModalData = {
      widgetComponent: this.elementFor($event.widget.id),
    };
  }

  onApplyComparisonModalClosed($event) {
    this.applyComparisonModalData = undefined;

    if ($event) {
      this.comparisonService.applyComparison($event.comparison, $event.widget, true);
    }
    this.dashboardStore.markDirty();
    this.showApplyComparisonModal = false;
  }

  public changeTimezone(): void {
    this.showTimezoneModal = true;
  }

  public onTimezoneModalClosed(timezone: string): void {
    this.showTimezoneModal = false;
    if (timezone) {
      this.timezoneService.timezone = timezone;
      this.setTimezoneData(timezone, this.timezoneService.timezoneOffset);
      if (!this.originalDashboard.payload.timezone && timezone === this.preferences.timezone) {
        delete this.dashboard.payload.timezone;
        delete this.dashboard.payload.timezoneOffset;
      } else {
        this.dashboard.payload.timezone = timezone;
        this.dashboard.payload.timezoneOffset = this.timezoneService.timezoneOffset;
      }
      this.dashboardStore.markDirty();

      const widgetsNeedingReload = this.dashboard.widgets.map((widget) => widget.id);

      widgetsNeedingReload.forEach((widgetId) => {
        const widgetComponent = this.elementFor(widgetId);
        if (widgetComponent) {
          widgetComponent.refreshWidget();
        }
      });
    }
  }

  private setTimezoneData(timezone?, timezoneOffset?) {
    this.dashboardTimezone = timezone ?? this.timezoneService.timezone;
    this.timezoneOffset = 'GMT' + (timezoneOffset ?? this.timezoneService.timezoneOffset);
    this.timezoneTooltip = `${this.dashboardTimezone} (${this.timezoneOffset})`;
  }

  async onOpenCreateDriverAnalysis($event: WidgetDriverAnalysisOptions) {
    this.collectionActions.onShowDriverAnalysisDialog($event.projectCollection, {
      analysisId: $event.dataSource.analysisId,
      filters: $event.filters,
      query: $event.query,
      weightFields: $event.weightFields,
      comparisonId: $event.comparisonId,
      comparisonFilters: $event.comparisonFilters,
      widgetGroupId: $event.widgetGroupId,
      widgetGroupFilters: $event.widgetGroupFilters,
      userSearch: $event.userSearch,
      fieldStatisticPayload: $event.fieldStatisticPayload,
    });
  }

  get isDashboardNew() {
    return this.dashboardId === 'new';
  }

  private setPinning(): void {
    if (this.dashboard.dashboard_version > 10) {
      this.pinningService.set(this.dashboard.payload.pins);
    }
  }

  private updateDateFields(source: Dashboard, target: Dashboard) {
    target.modified_at_date = source.modified_at_date;
    target.user_modified_at_date = source.user_modified_at_date;
  }

  ignoreWidgetChange(ignore: boolean): void {
    this.shouldIgnoreWidgetChange = ignore;
  }

  onBeforeExpansionPanelOpen(widgetId: string): void {
    this.isExpansionPanelShowing = true;
    this.expansionPanelShowingOnWidget = widgetId;

    if (this.dashboardUnsaved) {
      this.dashboardUnsaved = false;
      this.dashboardUnsavedEmit.emit(this.dashboardUnsaved);
    }
  }

  onExpansionPanelShown(panelShown: boolean, widgetContainer: ElementRef): void {
    this.isExpansionPanelShowing = panelShown;

    // Use requestAnimationFrame to ensure DOM updates are complete before scrolling
    requestAnimationFrame(() => {
      // Additional timeout to account for gridster reflow animations
      setTimeout(
        () =>
          (widgetContainer.nativeElement as HTMLDivElement).scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'center',
          }),
        DashboardViewComponent.SCROLL_DELAY_MS
      );
    });
  }

  onExpansionPanelOverlayClicked() {
    this.commentsHandlerService.hideExpansionPanel({
      widgetId: this.expansionPanelShowingOnWidget,
      panelType: this.expansionPanelTypeOnWidget,
    });
  }

  handleMaxWidthChange(newWidthWithPixels: string | null) {
    this.ratingModalWidth = newWidthWithPixels ?? this.DEFAULT_RATING_MODAL_WIDTH;
  }
}
